/* Diamond browse — the filter panel and results grid for /collections/diamonds.

   WHY THIS EXISTS SEPARATELY FROM stone-browse.js. Every other stone landing page
   filters through SHOPIFY's storefront filters: `collection.filters` gives Liquid
   the values and counts, and each chip is a link that reloads the page. Diamonds
   cannot do that — the collection is 7,991 products and Shopify renders ZERO
   filters over 5,000, so `collection.filters` comes back EMPTY and every row of
   that panel would draw its label and then nothing. So this panel is built from
   the pre-built shards instead (build_diamond_assets.py) and filters in the
   browser.

   🚨 IT IS THE SAME UI, DELIBERATELY, DOWN TO THE CLASS NAMES. `sb__row`,
   `sb__label`, `sb__scroll`, `sb-shape`, `sb-colour`, `sb-rng` and friends are all
   stone-browse.css's, and this file emits that markup rather than inventing a
   parallel look. A shopper moving from /collections/sapphires to
   /collections/diamonds should not be able to tell that one is server-filtered and
   the other is not. The consequence to respect: THIS FILE MUST NOT RESTYLE THOSE
   CLASSES — stone-browse.css owns them, it is shared with 21 live landing pages,
   and anything added here that fights it will show up there first.

   ⚠️ THE GRADE LADDERS ARE NOT DEFINED HERE. They come from `RB.SCALES`, exported
   by ring-builder-stones.js, which is the single definition and the one
   `verify_diamond_assets.py` parses. A local copy would be the chain_fit bug: two
   implementations of one rule that agree until someone edits one.

   ⚠️ SHAPE ICONS ARE CLONED, NOT DRAWN. `gem-shape-icon.liquid` is the canonical
   silhouette set and it is Liquid, so the section renders every icon once into a
   hidden sprite and this file clones from it. Redrawing them here would be a
   second set to keep in step with the geometry verifier. */
(function () {
  'use strict';
  var RB = window.RingBuilder;
  var root = document.querySelector('[data-db-root]');
  if (!RB || !root || !RB.SCALES) return;

  var el = RB.el, money = RB.money;
  var S = RB.SCALES;
  var COLOR = S.COLOR, CLARITY = S.CLARITY, CUT = S.CUT;

  // 🚨 ONE PANEL, TWO SHARD SETS. `/collections/diamonds` ships RB_DIAMONDS_META
  // (7,878 diamonds); `/collections/all-gemstones?view=stone-search` ships
  // RB_ALLSTONES_META (all 13,774, both kinds). The all-stones meta WINS when
  // present, and its presence is also what turns the kind toggle on — a page that
  // only has diamonds must not grow a "Colored" tab with nothing behind it.
  //
  // ⚠️ EVERY DIAMOND-ONLY BRANCH BELOW IS GATED ON `KIND`, NOT ON WHICH META
  // LOADED. The diamond page and the all-stones page in its diamond tab must render
  // the SAME rail — if they diverge, the divergence is a bug, not a feature.
  var META = window.RB_ALLSTONES_META || window.RB_DIAMONDS_META
             || { shards: {}, kinds: {} };
  var BOTH_KINDS = !!window.RB_ALLSTONES_META;
  // 🚨 ALL-KINDS SHAPE PAGES (owner 2026-09-18: "I don't want to lose filters ... Do not do anything that
  // negatively impacts the user experience. We found a way with the diamonds"). A shape collection past
  // Shopify's 5,000-product filter limit is bound to this panel instead of the landing page's native one,
  // and it must keep what that page offered: diamonds and colored stones in ONE grid, Shape as the SWITCHER
  // between the shape pages, and Cultured kept apart from Natural (a cultured pearl is never "natural" —
  // generate_stone_products.tags_for makes the same refusal). Everything that reads ALL_KINDS or SCOPE_SHAPE
  // is OPT-IN from the section's markup: the diamonds page, gem search and the builders never carry
  // `data-db-all-kinds` or `data-db-scope-shape`, so they run exactly the code they ran before.
  var ALL_KINDS = BOTH_KINDS && root.hasAttribute('data-db-all-kinds');
  var SCOPE_SHAPE = (root.getAttribute('data-db-scope-shape') || '').trim().toUpperCase();
  var SHAPE_LINKS = [];
  if (SCOPE_SHAPE) {
    try { SHAPE_LINKS = JSON.parse(root.getAttribute('data-db-shape-links') || '[]') || []; }
    catch (e) { SHAPE_LINKS = []; }
  }
  // 🚨 A SCOPED PAGE DRAWS ITS COLLECTION, NOT ITS SHAPE. The shards are cut by shape and the page is a
  // collection (`TAG EQUALS Round`): the ROUND shards also hold 52 calibrated pearls the collection does not,
  // and they carry no Featured or Best selling position at all. collection_ranks.py writes `rb-rank-<handle>.js`
  // in the same build as the shards (build_stone_search_assets.py): the members in Featured order (`h`), each
  // one's Best selling position (`b`, -1 = not placed; null when that walk failed), the shards that hold them,
  // the diamond/colored split, and `meta_built`, the stamp of the shard index it was judged against.
  // 🚨 IT FAILS CLOSED (Codex review-mu8bhmqf-4wbgsn). Only a rank file for THIS collection, built with THIS
  // shard index (`meta_built` equal to the meta's `built`) and well-formed, is used. Anything else (missing,
  // another build's, truncated) and this panel draws nothing: the page keeps Shopify's own grid, the right
  // stones in the right order without filters, rather than every stone of the shape or a stale population.
  var RANK = null, RANK_IX = null;
  if (SCOPE_SHAPE) {
    var rankWhy = rankProblem(window.RB_RANK, root.getAttribute('data-db-collection') || '', META.built);
    if (rankWhy) {
      failClosed(rankWhy);          // before the native grid is hidden: it simply stays on screen
      return;
    }
    RANK = window.RB_RANK;
    RANK_IX = {};
    for (var rki = 0; rki < RANK.h.length; rki++) RANK_IX[RANK.h[rki]] = rki;
  }
  // A scoped page that cannot draw its WHOLE collection draws none of it: the reason is recorded where a check
  // can read it (the console is invisible to one), this panel hides, and Shopify's own grid (hidden once this file
  // took over, or never) is put back. Used before the first draw (the rank file) and after it (a shard that did not
  // load, Codex review-mu8co8kj-1ts94n).
  function failClosed(why) {
    if (window.console && console.warn) console.warn('diamond-browse: ' + why + '; showing the collection\'s own grid instead');
    root.setAttribute('data-db-closed', why);
    root.hidden = true;
    if (native) native.hidden = false;
    if (FACETS_RENDER && typeof FacetFiltersForm === 'function') FacetFiltersForm.renderPage = FACETS_RENDER;
  }
  // Why a rank file cannot be used, or '' when it can. Every field the page reads is checked before anything is
  // hidden or drawn.
  function rankProblem(rk, want, metaBuilt) {
    if (!rk || typeof rk !== 'object') return 'no rank file';
    if (!want || rk.collection !== want) return 'the rank file is for "' + rk.collection + '", not "' + want + '"';
    if (!metaBuilt || rk.meta_built !== metaBuilt) {
      return 'the rank file was built with the shard index of ' + rk.meta_built + ', this page reads ' + metaBuilt;
    }
    if (!Array.isArray(rk.h) || !rk.h.length || typeof rk.h[0] !== 'string') return 'the rank file lists no members';
    if (rk.b !== null && !(Array.isArray(rk.b) && rk.b.length === rk.h.length)) {
      return 'the rank file\'s Best selling positions do not match its members';
    }
    if (!Array.isArray(rk.shards) || !rk.shards.length) return 'the rank file names no shards';
    if (!rk.kinds || typeof rk.kinds.d !== 'number' || typeof rk.kinds.c !== 'number') {
      return 'the rank file has no diamond/colored split';
    }
    return '';
  }

  // 🚨 THE SWAP HAPPENS HERE, NOT IN THE SECTION. RB.loadShard() and RB.rows() both
  // read window.RB_STONES_META — the shard registry and the shared dictionary — and
  // the diamond shards are byte-compatible with the builder's by design (see
  // build_diamond_assets.py), so this one line is the entire adaptation. It cannot
  // live in a DOMContentLoaded listener in the section: DEFERRED SCRIPTS RUN BEFORE
  // that event, so this file would already have asked for a shard against an
  // unswapped registry and been handed null. The builder is not mounted on this
  // page, so there is nothing to collide with.
  // ⚠️ ALL-STONES WINS. Both metas can never be on one page today, but the order
  // matters if that ever changes: RB.rows() resolves EVERY dictionary column
  // through whichever registry this points at, so pointing it at the wrong set
  // decodes real indices against the wrong vocabulary — the values come back
  // plausible and wrong (a species reading as a clarity grade), which is far worse
  // than coming back empty.
  if (window.RB_ALLSTONES_META) window.RB_STONES_META = window.RB_ALLSTONES_META;
  else if (window.RB_DIAMONDS_META) window.RB_STONES_META = window.RB_DIAMONDS_META;
  var panel = root.querySelector('[data-db-panel]');
  var grid = root.querySelector('[data-db-grid]');
  var countEl = root.querySelector('[data-db-count]');
  var activeEl = root.querySelector('[data-db-active]');
  var sortEl = root.querySelector('[data-db-sort]');
  var sprite = root.querySelector('[data-db-sprite]');

  /* ------------------------------------------------------------- density ---
     🚨 THE KEY IS THE THEME'S OWN, AND THAT IS THE POINT. facets.js's
     ViewModeController persists the desktop column count under
     `productDesktopViewCols`; sharing it means a shopper who chose 3-up on
     /collections/sapphires arrives here 3-up and back again, which is what "parity
     with the gems page" actually means to someone using the store. The VALUES are
     the theme's too (2/3/4/9), so neither side can write something the other
     cannot read.

     ⚠️ WIRED HERE, OUTSIDE THE SHARD PROMISE, ON PURPOSE. Everything else on this
     page waits for a network fetch of up to 87 KB; the buttons are static markup
     and the grid element already exists, so making them wait would leave a visibly
     live control inert for the whole load. Nothing here touches the rows.

     ⚠️ NOT `.view-mode__button`. See sections/diamond-search.liquid: facets.js
     claims that class, clones every button it finds to drop old listeners, and
     rebinds — so borrowing the name to borrow the theme's CSS would silently
     delete these handlers on any load where facets.js ran second, and point them
     at `#product-grid`, the hidden server-rendered grid, if it did not. */
  var DENSE_KEY = 'productDesktopViewCols', DENSE_OK = ['2', '3', '4', '9'];
  var denseBtns = root.querySelectorAll('.db-dense__b');

  function applyDensity(cols, persist) {
    if (DENSE_OK.indexOf(cols) < 0) cols = '4';
    grid.style.setProperty('--db-cols', cols);
    // Mirrored onto an attribute as well as the custom property: the nine-column
    // rule has to hide `.card__content`, and CSS cannot select on the VALUE of a
    // custom property.
    grid.setAttribute('data-db-cols', cols);
    Array.prototype.forEach.call(denseBtns, function (b) {
      b.setAttribute('aria-pressed', String(b.getAttribute('data-db-cols') === cols));
    });
    if (persist) { try { localStorage.setItem(DENSE_KEY, cols); } catch (e) { /* private mode */ } }
  }
  if (denseBtns.length) {
    var saved = '4';
    try { saved = localStorage.getItem(DENSE_KEY) || '4'; } catch (e) { /* private mode */ }
    applyDensity(saved, false);
    Array.prototype.forEach.call(denseBtns, function (b) {
      b.addEventListener('click', function () {
        applyDensity(b.getAttribute('data-db-cols'), true);
      });
    });
  }

  // The native grid is real, server-rendered, crawlable content and it is the
  // page's no-JS answer. It is hidden only once we are certain we can replace it —
  // i.e. here, after this file has run — so a visitor whose JS fails still gets a
  // browsable collection instead of an empty page.
  // Shopify wraps every section in `#shopify-section-<template key>`, and the key
  // for the native grid in collection.diamond-search.json is `product-grid`. Found
  // by id rather than by editing main-collection-product-grid.liquid, which renders
  // EVERY collection on the store and must not grow a diamond-specific hook.
  var native = document.getElementById('shopify-section-product-grid')
            || document.querySelector('[id^="shopify-section"][id$="product-grid"]');
  if (native) native.hidden = true;
  // QUICK VIEW ON EVERY CARD, on an all-kinds page (owner 2026-09-17, option B: the landing page this replaces draws
  // the theme's Quick View button on every buyable stone card). The button is CLONED from that hidden grid, so its
  // classes, wording, icon and spinner are the theme's own (snippets/card-product.liquid, the quick-buy style
  // setting) and the theme's <quick-view> element (theme.js) opens its one shared window from `data-product-url`.
  // No such button in the grid (Quick View switched off, or no buyable card on its first page) means none here
  // either: never a hand-made copy. Other pages keep their cards exactly as they were.
  var QV_TEMPLATE = (ALL_KINDS && native) ? native.querySelector('.card-product__media-inner .quick-add') : null;
  if (QV_TEMPLATE && !QV_TEMPLATE.querySelector('quick-view')) QV_TEMPLATE = null;
  function quickAdd(href) {
    if (!QV_TEMPLATE) return null;
    var node = QV_TEMPLATE.cloneNode(true);
    node.querySelector('quick-view').setAttribute('data-product-url', href);
    return node;
  }

  /* 🚨 THE THEME'S FACET SCRIPT MUST NOT ANSWER OUR BACK BUTTON (2026-09-16). assets/facets.js, loaded with that hidden
     grid, listens for every Back and Forward and re-renders the grid from `?section_id=` for the address it finds: a
     full render of a 10,000-product collection that nobody sees, and on gem search each one left more page-wide
     keyboard listeners behind (measured: 7 -> 10 over three Back steps). While every filter here was a page load it
     never met a history entry of ours; with no-reload filtering (render(), below) every filter is one. The grid is
     hidden, so its re-render has no purpose here and is switched off — only once this file has hidden it, so a
     visitor whose script failed keeps the theme's own. facets.js declares `class FacetFiltersForm` in a classic
     script (a global binding, not a window property) and on /collections/diamonds loads AFTER this file. */
  // The theme's own renderPage is kept, so a page that FAILS CLOSED (failClosed, above) hands Shopify's grid back
  // with its sort control working (Codex review-mu8dx3l6-52p3ul); a page already closed is never quietened.
  var FACETS_RENDER = null;
  function quietFacets() {
    if (typeof FacetFiltersForm !== 'function') return false;
    if (root.hasAttribute('data-db-closed')) return true;
    if (!FACETS_RENDER) FACETS_RENDER = FacetFiltersForm.renderPage;
    FacetFiltersForm.renderPage = function () {};
    return true;
  }
  if (native && !quietFacets()) document.addEventListener('DOMContentLoaded', quietFacets);

  /* 🚨 FROM HERE TO THE END OF THE FILE IS ONE FUNCTION, render(), AND IT RUNS ON EVERY FILTER (2026-09-16).
     Owner: filters must update without reloading the page, as they already do on the collection pages. This file
     always drew the whole tool from the address once per page load; it now draws it again whenever the address
     changes, and assets/sr-client-filters.js changes the address without a load (pushState) and calls it. The
     body below is deliberately NOT re-indented — it is the page as it always was, and 1,900 lines of whitespace
     would bury the handful that changed.
     ⚠️ ANYTHING THAT MUST HAPPEN ONCE PER PAGE LIVES ABOVE THIS LINE, OR IS GUARDED: a listener on an element that
     survives a draw (the panel, the sort <select>, the document) would otherwise be added once per filter and fire
     that many times. The two such places below are guarded (wireDropdowns, the sort select).
     `token` says whether this is the page load (`token.first`) and whether a newer filter has overtaken this draw
     (`token.stale()`); `token.calm` holds the panel until the shopper stops scrolling. */
  var FIRST = { first: true, calm: function (v) { return v; }, stale: function () { return false; } };
  var inPlace = null;   // SRClientFilters' handle once attached; null means every filter is a page load
  function render(token) {

  var q = new URLSearchParams(location.search);
  var num = function (k, d) { var v = parseFloat(q.get(k)); return isNaN(v) ? d : v; };
  var intp = function (k, d) { var v = parseInt(q.get(k), 10); return isNaN(v) ? d : v; };

  // The same patch applied two ways: `apply` NAVIGATES (a control the shopper just
  // operated), `hrefWith` composes the URL for a link (a chip they may middle-click,
  // copy or share). Both keep every other param, which is what `url_to_add` gives
  // the landing pages for free and what this page has to do by hand.
  function patched(patch) {
    var n = new URLSearchParams(location.search);
    Object.keys(patch).forEach(function (k) {
      if (patch[k] == null || patch[k] === '') n.delete(k); else n.set(k, patch[k]);
    });
    return n;
  }
  function apply(patch) { go(hrefWith(patch)); }
  // In place once sr-client-filters.js has taken this page; otherwise the page load it always was.
  function go(href) {
    if (!(inPlace && inPlace.go(href, { source: document.activeElement }))) location.href = href;
  }
  function hrefWith(patch) {
    var s = patched(patch).toString();
    return location.pathname + (s ? '?' + s : '');
  }

  // 🚨 A THIRD KIND OF LINK, AND IT EXISTS BECAUSE OF A REAL BUG (owner, 2026-08-09).
  // The Stone and Formation rows deliberately DROP every other facet — a clarity
  // range or a fancy hue means nothing once you switch to colored stones. They were
  // therefore built with a fresh `new URLSearchParams({origin: o})`, which threw
  // away every param not named in it… including `view`.
  //
  // On /collections/diamonds that is harmless: the collection is BOUND to the
  // diamond-browse template, so there is no `view` to lose. On
  // /collections/all-gemstones the tool is reached with `?view=stone-search`, and
  // dropping it fell straight back to the collection's real template — pressing
  // "Colored stones" landed the shopper on the gemstone HUB. It looked like the
  // toggle navigating somewhere sensible, which is why it needed reporting rather
  // than showing up as an error.
  //
  // STRUCTURAL params survive a reset; FACETS do not. `view` is the only structural
  // one today, and it is listed rather than inferred so adding another is a
  // deliberate act.
  var STRUCTURAL = ['view'];
  function resetHref(patch) {
    var n = new URLSearchParams();
    STRUCTURAL.forEach(function (k) {
      var v = q.get(k);
      if (v) n.set(k, v);
    });
    Object.keys(patch).forEach(function (k) {
      if (patch[k] != null && patch[k] !== '') n.set(k, patch[k]);
    });
    var s = n.toString();
    return location.pathname + (s ? '?' + s : '');
  }

  // Taken over once, on the page load. `lists` are the params this file reads as comma lists
  // (`q.get(…).split(',')` below): a chip adds or removes one value of them, so a tap made while an earlier one
  // is still loading its stones keeps both. `[data-sr-reset]` marks the links that mean "start again" — Clear
  // all and the Diamonds / Colored stones switch — which are followed exactly as written.
  if (token.first && window.SRClientFilters) {
    inPlace = SRClientFilters.attach({
      root: root, body: panel, grid: grid,
      toolbar: root.querySelector('.sb__foot'),
      drawer: root.querySelector('.sb__cb'),
      keep: STRUCTURAL,
      lists: ['origin', 'shape', 'fancy', 'offcolor', 'setting', 'cutstyle', 'treatment', 'quality',
              'sp', 'hue', 'country'],
      reset: '[data-sr-reset]',
      render: render
    });
  }

  // 🚨 FORMATION IS A FILTER, AND "NONE" MEANS ALL — THE SAME LESSON SHAPE LEARNED
  // ONE ROW DOWN (see the note below it). This used to be `q.get('origin')==='lab'
  // ? 'lab' : 'nat'`: exactly one formation, always, defaulting to natural, with no
  // way to express "either". The shards ARE keyed `SHAPE|origin`, which is why it
  // looked like a shard switch rather than a facet — but so is shape, and shape
  // stopped pretending years' worth of stones did not exist.
  //
  // What it cost, measured 2026-08-10 on the budget finder's own numbers: under
  // $1,000 the catalogue holds 6,110 diamonds, of which 5,091 are lab-grown. A
  // shopper landing here from "See all 6,110 diamonds in this budget" was shown
  // 1,019 — 17% — with nothing on the page saying a filter had been applied for
  // them. Under $250 it was 40 of 1,671.
  //
  // ⚠️ `?origin=lab` and `?origin=nat` STILL MEAN WHAT THEY ALWAYS DID, so every
  // link already in the wild — and the ring builder's own deep links — keep working.
  // The new state is the ABSENCE of the param, which was previously unreachable.
  var ORIGINS = ['nat', 'lab'];
  var originSel = (q.get('origin') || '').split(',')
    .map(function (s) { return s.trim().toLowerCase(); });
  // Rebuilt from ORIGINS rather than filtered in place: dedupes, drops junk, and
  // pins the order so `nat,lab` and `lab,nat` are one URL and not two.
  // On an all-kinds page Cultured is a third formation (the pearls), judged per stone, not per shard.
  var FORMS = ALL_KINDS ? ['nat', 'lab', 'cult'] : ORIGINS;
  originSel = FORMS.filter(function (o) { return originSel.indexOf(o) >= 0; });
  // What to actually SHOW. Both selected means the same thing as neither.
  // ⚠️ AN ALL-KINDS PAGE LOADS BOTH SHARD SETS for its one shape and narrows formation stone by stone
  // (formOf, in passes()), because Natural there must EXCLUDE the cultured pearls that share the natural
  // shards — a shard-level filter cannot tell them apart.
  var origins = ALL_KINDS ? ORIGINS.slice()
    : (originSel.length && originSel.length < ORIGINS.length) ? originSel.slice() : ORIGINS.slice();

  // The `origin` value to write into a URL: the shopper's own selection, or nothing
  // when they have not narrowed it. 🚨 NEVER the resolved `origins` list — writing
  // "nat,lab" would turn "I have not chosen" into a filter that looks chosen, and
  // the Formation chips would both light up on a page showing everything.
  function originParam() {
    return (originSel.length && originSel.length < FORMS.length)
      ? originSel.join(',') : null;
  }
  // KIND — diamond or colored. Only meaningful when the shard set holds both; on
  // the diamond page it is pinned to 'diamond' so nothing about that page changes.
  //
  // 🚨 `?gem=`, NOT `?type=`, AND THE REASON IS SEO, NOT TASTE. ring-builder-stones.js
  // calls this same choice `type` and matching it was the first instinct — but
  // SHOPIFY KEEPS `type` IN THE CANONICAL TAG and strips almost everything else.
  // Measured on this very page: `?type=color` canonicalises to
  // `/collections/all-gemstones?type=color` while `?gem=color`, `?kind=color` and
  // `?origin=lab` all canonicalise to the bare collection. So `type` would mint a
  // second canonical URL for one collection — a duplicate-content signal produced
  // by a filter chip, which is exactly the kind of thing nobody notices for months.
  // `gem` is not a Shopify-meaningful param, so the facet stays a facet.
  var KIND = !BOTH_KINDS ? 'diamond'
    : q.get('gem') === 'color' ? 'color'
    : (ALL_KINDS && q.get('gem') !== 'diamond') ? 'all' : 'diamond';
  var isD = function (r) { return r.species === 'Diamond'; };
  // Formation of ONE stone on an all-kinds page: a pearl is cultured whichever shard it came from.
  var formOf = function (r) { return r.species === 'Pearl' ? 'cult' : (r.origin === 'lab' ? 'lab' : 'nat'); };
  // ONE label per formation, for the Formation row, its active chips and the compare tray alike (Codex
  // review-mu8co8kj-1ts94n: a cultured pearl was compared as "Natural"). "Lab-Grown", capital G: owner rule.
  var FORM_LABEL = { nat: 'Natural', lab: 'Lab-Grown', cult: 'Cultured' };
  // The `gem` a reset keeps: the tab the shopper is on. On an all-kinds page no `gem` means ALL, so the
  // Diamonds tab has to say so.
  var gemKeep = function () { return KIND === 'color' ? 'color' : (ALL_KINDS && KIND === 'diamond') ? 'diamond' : null; };

  // 🚨 SHAPE IS A MULTI-SELECT FILTER, AND "NONE" MEANS ALL — IT IS NOT A SHARD KEY.
  // This used to default to ROUND and hold exactly one shape, because that is how
  // the shards are KEYED (SHAPE|origin) and the ring builder only ever needs one at
  // a time. On a browse page that made Round look pre-chosen, gave no way to clear
  // it, and hid the other 6,900 diamonds behind a tile the shopper never asked to
  // press. Selection is now a LIST, empty by default, and the shard key is an
  // implementation detail this resolves against rather than something the URL
  // states. Every shape a shopper ticks is one more shard to fetch; with none
  // ticked we fetch them all for the origin, which is what "all diamonds" costs.
  // Every shape held in ANY selected formation. Deduped — a shape usually exists in
  // both, and offering it twice would double its tile and its count.
  var shapesForOrigin = Object.keys(META.shards)
    .filter(function (k) { return origins.indexOf(k.split('|')[1]) >= 0; })
    .map(function (k) { return k.split('|')[0]; })
    .filter(function (s, i, a) { return a.indexOf(s) === i; });
  var picked = (q.get('shape') || '').split(',')
    .map(function (s) { return s.trim().toUpperCase(); })
    .filter(function (s) { return s && shapesForOrigin.indexOf(s) >= 0; });
  // The shards actually needed this render: the ticked shapes, or every shape —
  // in each selected formation. BOTH axes are "none means all", so a shopper who
  // has narrowed neither pulls the whole set, which is what "all gemstones" costs
  // (~790 KB gzipped against ~350 for one formation). Anything they tick cuts it.
  var wanted = picked.length ? picked : shapesForOrigin;
  // 🚨 A SCOPED SHAPE PAGE LOADS ITS OWN SHAPE AND NOTHING ELSE — never "no pick means every shape", which
  // would put 15,000 stones on the Round page. A shape the registry lacks yields no shards: an empty grid.
  // It loads the shapes that HOLD its members (a member filed under another shape is still the collection's), and
  // the pool keeps the members alone. A scoped page always has a usable rank file here (it failed closed above).
  if (SCOPE_SHAPE) wanted = shapesForOrigin.filter(function (s) {
    return RANK.shards.some(function (k) { return String(k).split('|')[0] === s; });
  });
  var wantedShards = [];
  wanted.forEach(function (s) {
    origins.forEach(function (o) {
      if (META.shards[s + '|' + o]) wantedShards.push({ shape: s, origin: o });
    });
  });

  function shapeIcon(name) {
    var src = sprite && sprite.querySelector('[data-shape="' + name + '"] svg');
    return src ? src.cloneNode(true) : null;
  }

  // The species swatches and, just as importantly, THEIR ORDER — read from the
  // section's second sprite, which renders the `gem_map` setting in its own order.
  // Count order would be the obvious alternative and is wrong here: the row would
  // reshuffle under the cursor every time a shopper touched another facet.
  var gemSprite = document.querySelector('[data-db-gems]');
  function gemNode(name) {
    return gemSprite && gemSprite.querySelector('[data-gem="' + name + '"]');
  }
  function gemDot(name) {
    var n = gemNode(name);
    return n ? n.getAttribute('data-dot') : null;
  }
  // The species PHOTOGRAPH — its collection image, resolved live in the section and
  // carried here on the sprite (stone-search.liquid). Null for a species whose line
  // predates the third `gem_map` field or whose collection has no image; the tile
  // falls back to the swatch, which is what this row drew for every species until
  // 2026-08-19.
  function gemImg(name) {
    var n = gemNode(name);
    return (n && n.getAttribute('data-img')) || null;
  }
  var GEM_ORDER = gemSprite
    ? [].map.call(gemSprite.querySelectorAll('[data-gem]'),
                  function (n) { return n.getAttribute('data-gem'); })
    : [];

  // THE TWO POPULATION PHOTOGRAPHS — a diamond for Diamonds, a ruby for Colored
  // stones (owner, 2026-08-19: *"do away with the pill ... a diamond for Diamonds
  // and maybe a nice ruby for colored stones"*). Third sprite, same mechanism as
  // the two above: resolved live in Liquid from the `diamonds` and `rubies`
  // collections' own images, so this row shows the same picture the mega menu and
  // the species tiles do and none of them can go stale.
  //
  // 🚨 IT IS ITS OWN SPRITE AND MUST NOT BE FOLDED INTO `data-db-gems`. That one
  // renders the `gem_map` setting, whose lines are asserted one-for-one against
  // stone-browse.liquid's `species` list — `verify_stone_landing.py` FAILS on any
  // divergence — and Diamond is deliberately absent from it because that row only
  // exists on the colored tab. Adding a Diamond line to reach one photograph would
  // break the parity check and put a Diamond chip on all 31 species pages.
  //
  // ⚠️ NULL IS A SUPPORTED ANSWER, and it is the state /collections/diamonds is in:
  // `diamond-search.liquid` emits no such sprite, the Stone row does not render
  // there anyway (BOTH_KINDS is false), and any surface without it falls back to
  // the `.sb-type` dot pill — exactly what this row drew until today.
  var kindSprite = document.querySelector('[data-db-kinds]');
  function kindImg(key) {
    var n = kindSprite && kindSprite.querySelector('[data-kind="' + key + '"]');
    return (n && n.getAttribute('data-img')) || null;
  }
  var titleCase = function (s) {
    return String(s || '').toLowerCase().replace(/(^|\s)\w/g, function (m) {
      return m.toUpperCase();
    });
  };

  // ⚠️ THE SWATCH NEEDS AN INLINE `--sw` AS ITS FLOOR. stone-browse.css sets
  // `background-image: var(--sw)` on `.sb-colour__sw` and defines NO
  // `.sb-colour--<hue>` rules at all, so emitting the modifier class alone renders a
  // transparent circle — which is exactly what it did first time round.
  //
  // ⚠️ SINCE stone-list.css JOINED THIS PAGE (2026-08-05, with compare) THE SHARED
  // PALETTE WINS WHERE IT HAS A LINE, AND THAT IS THE INTENDED ORDER, NOT A CLASH.
  // That sheet carries `--co-*` for 26 colours and applies them through
  // `.sb-colour--blue .sb-colour__sw`, which is (0,2,0) against this custom
  // property's (0,1,0). So every hue the store has a house colour for now draws the
  // store's colour — the landing pages' own rail, which is the point of loading it —
  // and the table below is what a hue nobody has listed falls back to. Do not
  // "promote" these to !important to make them win: two palettes disagreeing on
  // Blue is the thing being fixed.
  var HUE_SW = {
    Green: 'linear-gradient(145deg,#3f8f63,#12482c)',
    Pink: 'linear-gradient(145deg,#e6a5a8,#bf6a72)',
    Blue: 'linear-gradient(145deg,#3d6fb5,#14315f)',
    Yellow: 'linear-gradient(145deg,#e8c05a,#b3831f)',
    Orange: 'linear-gradient(145deg,#e2a15c,#b3651f)',
    Brown: 'linear-gradient(145deg,#b08867,#6b4a2e)',
    Gray: 'linear-gradient(145deg,#c9ccce,#84898d)',
    Black: 'linear-gradient(145deg,#5a5a5a,#141414)',
    Purple: 'linear-gradient(145deg,#8b6ab5,#482d6f)',
    Red: 'linear-gradient(145deg,#c0304a,#7a0f22)',
    // Two of Stuller's hue names are a SINGLE word, so `fancyHue` returns them
    // whole and they have no colour-word entry to fall back on. Without these both
    // rendered the `Gray` default, which is how "Champagne" came to show as a grey
    // dot. Every other enhanced hue ends in a colour word already handled above
    // ("Apple Green" -> Green, "Aqua Blue" -> Blue, "Burnt Orange" -> Orange).
    Champagne: 'linear-gradient(145deg,#e7d3ac,#c0a06a)',
    Cognac: 'linear-gradient(145deg,#c08a52,#7d4a1e)'
  };

  /* ------------------------------------------------------- 360 turntable ---
     The same hover-to-spin viewer the other 21 landing pages get. Those build it
     in Liquid (`card-stone-spin.liquid`); a JS-rendered grid cannot call a snippet,
     so the element is constructed here to the SAME contract — `<stone-hover-spin
     class="shs" data-src=… style="--shs-zoom:…">` wrapping `.shs__v` and
     `.shs__badge`. `stone-hover-spin.js` defines the custom element and upgrades it
     automatically on insert, so nothing needs re-initialising after paging.

     ⚠️ THE ZOOM CONSTANTS ARE COPIED FROM THAT SNIPPET AND MUST STAY IN STEP.
     Stuller's turntable leaves the stone filling ~37% of frame, so an un-zoomed clip
     makes the stone visibly SHRINK the moment you hover — it reads as a bug. The
     1.35 cap is deliberate: zoom is centre-origin and MULTIPLIES Stuller's
     off-centre framing, and at 2.0 an elongated stone swept a tip past the edge. A
     stone with no length_mm gets 1.0.
     🚨 REMOVED AND RESTORED THE SAME DAY, 2026-08-05 — do not delete it again on the
     strength of "the hover should be unedited". It was, and the rendered result is
     why it came back: 78.6% of diamonds sit at the cap, so the stone dropped to
     about half the still's size on hover across nearly the whole catalogue. Owner
     saw the before/after and chose to restore it. What DID stay fixed is the white
     plate behind the clip (stone-hover-spin.css) — that was the actual bug.
     🚨 AND ALL OF THAT IS NOW ONLY THE FALLBACK. `stone-hover-spin.js` overrides
     `--shs-zoom` from `assets/spin-fit.js`, the MEASURED framing of every Stuller
     turntable, as each element upgrades — so what is computed here survives only
     for a stone measured after the last `measure_spin_fit.py` run. It is left in
     place because that case is real (a fresh import) and a wrong size beats none.

     ⚠️ ON THIS PAGE THE FALLBACK IS NOT MERELY CONSERVATIVE, IT CLIPS. Diamonds
     are filmed on a MACRO rig — the stone already fills 0.40-0.91 of the frame —
     so 1.35 draws it at 0.92-1.14 of the tile and the corners and tips leave the
     card. 13 of 22 sampled diamonds were doing that on the live site before the
     measured map shipped (2026-08-05). The colour cohort has the OPPOSITE problem
     from the same constants. That is why this cannot be fixed by tuning the three
     numbers below, and why they must not be "improved" on the strength of one
     cohort looking better. */
  var SPIN_FIELD_MM = 21.4, SPIN_TARGET_FILL = 0.62, SPIN_ZOOM_MAX = 1.35;
  function spinZoom(lenMm) {
    if (!lenMm) return 1;
    var z = SPIN_TARGET_FILL / (lenMm / SPIN_FIELD_MM);
    return Math.max(0.4, Math.min(SPIN_ZOOM_MAX, z));
  }
  // The card image, emitting the theme's OWN initial classes and nothing more —
  // `img-loading`/`img-loaded` belong to the theme's loader, not to us. The only
  // thing added here is the cached-image case: when a browser already holds the
  // file, `load` fires before any listener can attach, so `complete` is checked
  // synchronously. Without that the fade-in never resolves and the image stays at
  // scale(1.1) — see the note at the call site.
  function markLoaded(img) {
    var done = function () {
      img.classList.remove('img-loading');
      img.classList.add('img-loaded');
    };
    if (img.complete && img.naturalWidth) done();
    else {
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    }
  }
  // 🚨 ASK THE CDN FOR A CARD-SIZED IMAGE, NOT THE ORIGINAL. The shard stores
  // Shopify's raw `featuredMedia.image.url`, which is the FULL master — 1600x1600
  // here. Rendering that into a ~302px slot, 48 at a time, downloaded ~25x more
  // than the grid can show and left every image `complete: false` for seconds. That
  // is not just slow: the theme holds a still at `opacity: 0` while `img-loading` is
  // on it, so the card showed NOTHING until the master arrived, and the 360 video
  // layered over it was what a shopper actually saw. A "cropped photo" that was
  // really a video over an invisible one. (The zoom that made it read as "cropped"
  // is gone as of 2026-08-05 — see the turntable note above — but the loading
  // failure it exposed is real and this is still why the CDN is asked for a
  // card-sized file.) The theme's own cards request `?crop=center&width=&height=`;
  // this does the same. 600 is 2x the widest slot, so it stays sharp on retina.
  function sized(url, px) {
    if (!url) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') +
           'width=' + px + '&height=' + px + '&crop=center';
  }
  // 🚨 A DEAD IMAGE URL MUST FAIL QUIETLY. The shard bakes each product's image URL,
  // and `fix_stone_images.py` DELETES the old media once the recut one is attached —
  // so between a recut and the next shard push those URLs 404. The browser's default
  // rendering of that is the worst possible one: a broken-image glyph plus the full
  // `alt` text spilling across the tile in body copy, which is what the owner saw
  // twice on 2026-08-05 (green lab diamonds, then a 2.05 ct round).
  // This cannot be prevented from the theme — the fix is to batch recuts and push
  // the shards after each (see CATALOG_STATE.md) — but the theme decides how bad the
  // gap LOOKS. On error the alt text is dropped and the tile is left as an empty
  // plate the size it would have been, so the row keeps its rhythm and the card is
  // still clickable through to the product page, where the image is always correct.
  // `data-img-failed` is left on for anyone auditing how many tiles are in this
  // state; `audit_stone_image_coverage.py` is the read-only check for real gaps.
  function imgEl(r) {
    var img = el('img', { src: sized(r.img, 600), alt: r.title, loading: 'lazy',
                          width: '600', height: '600',
                          class: 'motion-reduce lazy-image' });
    img.addEventListener('error', function () {
      img.setAttribute('data-img-failed', '1');
      img.removeAttribute('alt');           // stop the title rendering as body copy
      img.alt = '';
    }, { once: true });
    markLoaded(img);
    return img;
  }

  function spinViewer(r) {
    if (!r.video) return null;                 // no clip filmed — still only, no badge
    var host = el('stone-hover-spin', {
      class: 'shs', 'data-src': r.video,
      style: '--shs-zoom: ' + spinZoom(r.l).toFixed(4) + ';'
    });
    var v = el('video', { class: 'shs__v', muted: 'muted', loop: 'loop',
      playsinline: 'playsinline', preload: 'none',
      disablepictureinpicture: 'disablepictureinpicture',
      tabindex: '-1', 'aria-hidden': 'true' });
    v.muted = true;                            // the attribute alone does not autoplay
    var badge = el('button', { type: 'button', class: 'shs__badge',
      'aria-label': r.title + ' — play 360 degree rotating view' });
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('aria-hidden', 'true');
    svg.innerHTML = '<path d="M3 12a9 9 0 1 0 3-6.7"></path><path d="M3 4v5h5"></path>';
    badge.appendChild(svg);
    badge.appendChild(el('span', { text: '360°' }));
    host.appendChild(v);
    host.appendChild(badge);
    return host;
  }

  /* ------------------------------------------------------------ a panel row */
  // `help` is a sr-facet-help.js key (2026-09-03) in the slot the `<em>` sub-line
  // used to take. The sub-lines came off this rail and the builders' together —
  // the two copies of the same sentence had already drifted — and the copy is
  // one map on the engine, drawn as a tap-to-open panel beside the label. An
  // unknown key draws nothing; null draws nothing.
  function row(label, help, kids, mod) {
    var lab = el('span', { class: 'sb__label' }, [document.createTextNode(label)]);
    // ⚠️ GUARDED: for ~20 minutes after a release Shopify's caches can pair this file
    // with the previous ring-builder.js, which has no attachHelp. No "?" beats no page.
    if (RB.attachHelp) RB.attachHelp(lab, help);
    return el('div', { class: 'sb__row' + (mod ? ' ' + mod : '') },
      [lab, el('div', { class: 'sb__scroll', role: 'list' }, kids)]);
  }

  /* --------------------------------------------------------- a dropdown ----
     The landing pages' `sb-dd-chips` / `sb-dd-band` snippets, rebuilt in JS. Same
     markup and therefore the same stone-browse.css: a <details> whose <summary> is
     the trigger and whose panel is absolutely positioned.

     ⚠️ THE TRIGGER PRINTS THE APPLIED VALUE, NOT JUST THE LABEL — sb-dd-chips.liquid
     states why and it is worth repeating: a dropdown reading only "Cut style" while
     quietly filtering is hidden state narrowing the results, which is exactly what
     the "More filters" badge existed to prevent. One active value prints itself,
     several print a count.

     ⚠️ NATIVE <details>, NOT A JS POPUP. Opening, closing, keyboard operation and
     the expanded state are all the browser's. `wireDropdowns` below adds only the
     two things a floating popup needs and a bare <details> has no opinion about. */
  // ⚠️ THE DROPDOWN SHELL AND ITS "Any" / "N selected" SUMMARY MOVED TO THE ENGINE
  // when the builders' rail needed the identical control. Call sites use
  // `RB.dropdown` / `RB.ddValue` DIRECTLY rather than through a local alias: a
  // `var` alias is assigned when this line runs, not when the file loads, and this
  // file has already shipped one bug of exactly that shape (`priceFacet`, called
  // above its own assignment, drew two rows and dropped six facets).

  /* A LIST-FILTER DROPDOWN. `values` is [[value, label, count], …] already ordered.
     ⚠️ `skip` IS AN EXCLUSION AND MUST NEVER BECOME AN INCLUSION — the same lesson
     the colour row carries and the reason CLAUDE.md warns about hardcoded `tag:`
     lists. A hand-written list of values to SHOW silently hides whatever Stuller
     adds next; this iterates what the shards actually contain and drops only what
     is named. (Riente and Duchess Faceted-Cut are 14 and 13 stones respectively and
     arrived without anyone adding them here — that is the behaviour being bought.)
     A value with no stones is never drawn UNLESS it is applied, because an applied
     value has to stay reachable so it can be switched off. */
  // ⚠️ DELEGATES TO THE ENGINE (2026-09-06). The markup, the "Any" / "N selected"
  // summary and the zero-count rule now live in `RB.ddChips`, because the builders'
  // rail needed the identical control and a second copy is how one of them quietly
  // stops matching the other. What stays here is the only thing that is genuinely
  // this rail's: its chips are real `<a href>`, built through `hrefWith` so they
  // are shareable and crawlable and so they cannot mint a URL that drops `view`.
  // `help` is a sr-facet-help.js key or null — the "?" beside the trigger (2026-09-14).
  function chipsDropdown(label, sub, values, applied, param, skip, help) {
    return RB.ddChips({
      label: label, sub: sub, values: values, applied: applied, skip: skip || [], help: help,
      hrefFor: function (next) {
        var patch = {};
        patch[param] = next.length ? next.join(',') : null;
        return hrefWith(patch);
      }
    });
  }

  /* CLOSE ON AN OUTSIDE CLICK, AND ON ESCAPE — the only two behaviours a floating
     popup needs that <details> does not do for itself.

     🚨 THIS IS ~15 LINES REBUILT RATHER THAN `stone-browse.js` LOADED, AND THAT IS
     NOT LAZINESS. That file's `relocateTools()` looks for
     `facet-filters-form.facets-vertical-sort` and MOVES it into `.sb__foot`. That
     element exists on this page — inside the server-rendered collection grid this
     file hides — so loading stone-browse.js here would drag the theme's dead sort
     control into our toolbar, where it would submit `sort_by` to Shopify and
     re-sort a collection this page does not draw. Everything below is an
     enhancement: with it dead the dropdowns still open, still close on their own
     trigger, and the filters inside still work, because they are links. */
  function wireDropdowns(scope) {
    // ⚠️ ONCE PER PAGE (2026-09-16): the panel and the document outlive every redraw, and render() runs on
    // every filter. Unguarded, each filter added another copy of all three listeners.
    if (scope.hasAttribute('data-db-dd-wired')) return;
    scope.setAttribute('data-db-dd-wired', '');
    // One open at a time. Two overlapping popups in a wrapping row can cover each
    // other, and the second one's trigger is then unreachable.
    scope.addEventListener('toggle', function (e) {
      var dd = e.target;
      if (!dd.classList || !dd.classList.contains('sb-dd') || !dd.open) return;
      Array.prototype.forEach.call(scope.querySelectorAll('.sb-dd[open]'), function (o) {
        if (o !== dd) o.open = false;
      });
    }, true);   // capture: `toggle` does not bubble
    document.addEventListener('click', function (e) {
      Array.prototype.forEach.call(scope.querySelectorAll('.sb-dd[open]'), function (dd) {
        if (!dd.contains(e.target)) dd.open = false;
      });
    });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      Array.prototype.forEach.call(scope.querySelectorAll('.sb-dd[open]'), function (dd) {
        dd.open = false;
        var s = dd.querySelector('.sb-dd__btn');
        if (s && dd.contains(document.activeElement)) s.focus();
      });
    });
  }

  /* ------------------------------------------------- ordered-scale slider ---
     Two overlaid native range inputs on one track — the same construction
     stone-browse.css already styles for the carat and millimetre bands, so it
     needs no library and inherits the panel's look exactly. Values are INDICES
     into an ordered ladder, which is what makes "G and better" expressible.     */
  // ⚠️ SPLIT IN TWO: `rangeControl` builds the slider, `scaleRow` wraps it in a
  // labelled panel row. The dropdowns need the CONTROL ONLY — their <summary> IS the
  // label, and a row's label column inside a 260px popup would leave the track about
  // 90px wide. That is the same split `sb-band-row.liquid` expresses with its `bare`
  // flag, and it is why the two look identical on both pages.
  // 🚨 THE BODIES LIVE ON THE ENGINE NOW (2026-09-02): `RB.rangeControl` and
  // `RB.rangeRow` in assets/ring-builder.js ARE this control, moved there so the
  // ring/pendant builder's gemstone rail draws the identical slider from one
  // implementation. These two keep the panel's own names for them and nothing
  // else — the markup, the half-open bounds() clamp, the `pristine` readout rule
  // and the ladder are all documented at the engine. Grow the control there.
  function rangeControl(label, labels, lo, hi, onChange, fmt, showScale, initial) {
    return RB.rangeControl(label, labels, lo, hi, onChange, fmt, showScale, initial);
  }

  function scaleRow(label, help, labels, lo, hi, onChange, fmt, showScale, initial) {
    return RB.rangeRow(label, help, labels, lo, hi, onChange, fmt, showScale, initial);
  }

  /* ------------------------------------------------------------------ data */
  // Skeleton first: with no shape ticked this fetches every shard for the origin,
  // which is a real download, and an empty grid with no explanation reads as a
  // broken page rather than a loading one.
  // ⚠️ ON THE PAGE LOAD ONLY. A later filter keeps the stones on screen, dimmed, until the new ones are drawn:
  // swapping them for eight skeletons would shorten the page under a shopper scrolled into the results.
  if (token.first) {
    grid.innerHTML = '';
    for (var sk = 0; sk < 8; sk++) grid.appendChild(el('li', { class: 'db-skel' }));
  }

  return Promise.all(wantedShards.map(function (w) { return RB.loadShard(w.shape, w.origin); }))
    .then(token.calm)
    .then(function (shards) {
    // A newer filter has overtaken this one while its stones loaded: that draw wins, this one paints nothing.
    if (token.stale()) return;
    // A scoped page draws its collection from EVERY shard that holds it, or not at all: a shard that failed to load
    // resolves to null, and dropping it would show part of the collection as if it were all of it.
    if (RANK && shards.some(function (sh) { return !sh; })) {
      failClosed('a stone data file did not load (' + wantedShards.filter(function (w, i) { return !shards[i]; })
        .map(function (w) { return w.shape + '|' + w.origin; }).join(', ') + ')');
      return;
    }
    // RB.rows() stamps each row with its own shard's `shape`, so a merged pool
    // still knows which shape every stone is — nothing is lost by concatenating.
    var all = [];
    shards.filter(Boolean).forEach(function (sh) {
      RB.rows(sh).forEach(function (r) {
        if (!r.available) return;
        // A scoped page with a rank file: the collection's members only, in the collection's own two orders.
        // RB.rows builds fresh objects on every call, so stamping the ranks here touches no cached shard.
        if (RANK) {
          var ix = RANK_IX[r.handle];
          if (ix == null) return;
          r.featOrder = ix;
          r.bestOrder = (RANK.b && RANK.b[ix] >= 0) ? RANK.b[ix] : null;
        }
        // The kind split, and it is the whole toggle. `species === 'Diamond'` is an
        // exact compare — build_stone_search_assets.py takes the species from
        // `custom.gem_type`, and verify_stone_search_assets.py fails the build on
        // any near-miss spelling, because a "Diamonds" would silently land every
        // one of them in the colored tab where the four-C rail does not apply.
        if (BOTH_KINDS && KIND !== 'all' && (KIND === 'diamond') !== isD(r)) return;
        all.push(r);
      });
    });
    all.forEach(function (r) {
      r._cut = S.CUT_MAP[String(r.cut || '').toLowerCase()];
      // BANDS RESOLVE ONTO THE LADDER — "G-H" is an H, not an "other colour".
      // A bare COLOR.indexOf is what put 410 near-colourless stones in the
      // "warmer than K" row and hid them from the slider. See S.colIndex.
      r._col = S.colIndex(r.color);
      // Same story on the clarity axis: 459 stones carried a value the bare ladder
      // missed — Stuller's casing (`Si1`), its families (`Vs`, `Si`) and its bands
      // (`Si2-Si3`) — and `passes()` skips its guard on -1, so every one of them
      // passed the clarity slider at every position. See S.claIndex.
      r._cla = S.claIndex(r.clarity);
      r._fancy = S.isFancy(r.color);
      r._hue = r._fancy ? S.fancyHue(r.color) : null;
      r._off = !r._fancy && S.isOffScale(r.color);   // e.g. "U-V" — see the chip row
      // Bucketed to "L–Z" for real D-Z designations, raw value otherwise. Filtering
      // compares this key, so one chip can hold several grades.
      r._offKey = r._off ? S.offKey(r.color) : null;
    });

    var prices = all.map(function (r) { return r.price; });
    var carats = all.map(function (r) { return r.carat; }).filter(Boolean);
    var pMin = prices.length ? Math.floor(Math.min.apply(null, prices)) : 0;
    var pMax = prices.length ? Math.ceil(Math.max.apply(null, prices)) : 1000;
    var cMin = carats.length ? Math.min.apply(null, carats) : 0.2;
    var cMax = carats.length ? Math.max.apply(null, carats) : 13;
    // Millimetres, from the pool for the same reason carat and price are: the ends
    // of a slider have to be the ends of what is actually on this page, or dragging
    // to the extreme leaves stones outside the track. `L` and `W` are 100% filled
    // over all 7,890 diamonds (build_diamond_assets.py's coverage table), so unlike
    // carat there is no `.filter(Boolean)` case worth worrying about — the guard
    // below is there for a future shard, not for today's data.
    var lens = all.map(function (r) { return r.l; }).filter(Boolean);
    var wids = all.map(function (r) { return r.w; }).filter(Boolean);
    var lMin = lens.length ? Math.min.apply(null, lens) : 1;
    var lMax = lens.length ? Math.max.apply(null, lens) : 30;
    var wMin = wids.length ? Math.min.apply(null, wids) : 1;
    var wMax = wids.length ? Math.max.apply(null, wids) : 30;

    var st = {
      col: [intp('colmin', 0), intp('colmax', COLOR.length)],
      cla: [intp('clmin', 0), intp('clmax', CLARITY.length)],
      cut: [intp('cutmin', 0), intp('cutmax', CUT.length)],
      price: [num('pmin', pMin), num('pmax', pMax)],
      carat: [num('ctmin', cMin), num('ctmax', cMax)],
      len: [num('lmin', lMin), num('lmax', lMax)],
      wid: [num('wmin', wMin), num('wmax', wMax)],
      fancy: (q.get('fancy') || '').split(',').filter(Boolean),
      off: (q.get('offcolor') || '').split(',').filter(Boolean),
      setting: (q.get('setting') || '').split(',').filter(Boolean),
      cutstyle: (q.get('cutstyle') || '').split(',').filter(Boolean),
      treatment: (q.get('treatment') || '').split(',').filter(Boolean),
      quality: (q.get('quality') || '').split(',').filter(Boolean),
      // COLORED-TAB facets. Parsed unconditionally so the state shape does not
      // change between tabs — `passes()` skips an empty list, so these cost the
      // diamond page nothing.
      sp: (q.get('sp') || '').split(',').filter(Boolean),
      hue: (q.get('hue') || '').split(',').filter(Boolean),
      country: (q.get('country') || '').split(',').filter(Boolean),
      // A scoped shape page opens in its COLLECTION's default order (Best selling on the Round page, as the
      // landing page did); only that section emits the attribute, so every other page still opens on Featured.
      // It also honours Shopify's own `?sort_by=`, which is what the landing page it replaces wrote into every
      // sorted link and bookmark (its values are this table's keys); this page's own control writes `sort`.
      sort: q.get('sort') || (SCOPE_SHAPE && q.get('sort_by')) || root.getAttribute('data-db-default-sort') || 'manual'
    };
    var colWideOpen = st.col[0] === 0 && st.col[1] >= COLOR.length;
    var claWideOpen = st.cla[0] === 0 && st.cla[1] >= CLARITY.length;
    // Whether the carat range is narrowed: the same test that raises its active chip below.
    var caratOn = st.carat[0] > cMin || st.carat[1] < cMax;

    /* 🚨 EVERY FACET COUNT IS COUNTED AGAINST THE OTHER FACETS, NOT AGAINST THE POOL.
       Owner, 2026-08-06, with two screenshots: tick Fancy color -> Black (25 stones)
       and the Treatment dropdown still reads "None 2,156 · Color Enhanced 93". Every
       count on this panel was computed off `all` — the whole loaded shard set — so
       no number ever responded to any other control. The coloured-stone pages do not
       have this bug because they are Shopify-native faceted filters, which return
       counts already narrowed by the rest of the query; this panel is client-side and
       has to do that arithmetic itself.

       `except` names the facet GROUP to leave out, so a facet never narrows its own
       counts. That is what lets a shopper see the other options in the row they are
       using: with Black ticked, Green must still read 21 (its count when Black is not
       ticked), or the row collapses to the one value already chosen and there is no
       way to switch. Everything OTHER than the named group still applies.

       ⚠️ THE THREE COLOUR CONTROLS ARE ONE GROUP. Fancy color, Other color and the
       D-K slider are three renderings of a single axis and the branch below is
       mutually exclusive across them, so they are excluded together — narrowing one
       must not renumber its siblings on the same axis.

       ⚠️ SHAPE AND ORIGIN ARE DELIBERATELY NOT PART OF THIS. Their counts come from
       `META.shards[...].n`, the registry, because their shards are not loaded — that
       is the entire point of sharding (first load 100 KB gz against 529 KB for the
       set). Making them responsive would mean fetching all 25 shards on every visit
       to renumber tiles the shopper has not clicked. They stay registry counts. */
    function passes(r, except) {
      if (r.price < st.price[0] || r.price > st.price[1]) return false;
      if (ALL_KINDS && except !== 'origin' && originSel.length && originSel.length < FORMS.length
          && originSel.indexOf(formOf(r)) < 0) return false;
      // The three colored-tab facets. Each is skipped when its own row is being
      // counted (`except`), which is what makes a chip's number mean "how many if I
      // add this" rather than "how many are already showing".
      if (except !== 'sp' && st.sp.length && st.sp.indexOf(r.species) < 0) return false;
      if (except !== 'hue' && st.hue.length && st.hue.indexOf(r.hue) < 0) return false;
      if (except !== 'country' && st.country.length
          && st.country.indexOf(r.country) < 0) return false;
      // 🚨 TRUTHY, NOT `!= null` — 0 MEANS "NOT MEASURED", AND THE TWO SIDES OF THIS
      // COMPARISON HAVE TO AGREE ON THAT. The bounds above are built with
      // `.filter(Boolean)`, which drops a 0 — so `wMin` is the smallest NON-ZERO
      // width on the page. This test then said `0 != null`, treated the 0 as a real
      // measurement, and dropped every such stone for being narrower than the
      // narrowest stone. One expression called 0 absent and the next called it
      // present.
      //
      // Measured 2026-08-10: 208 colored stones carry `W = 0` (round stones whose
      // diameter filled `L` and left `W` unset). They were unreachable in this grid
      // AT EVERY FILTER SETTING, including none — 5,658 rendered of 5,866, and 47 of
      // them sat under $1,000, which is exactly the gap between what the budget
      // finder promised and what this page delivered. NO DIAMOND IS AFFECTED: their
      // L and W are 100% filled, which is why this survived on the page it was
      // written for.
      //
      // ⚠️ A stone cannot be 0 mm wide, so 0 is never data. Treated as unmeasured it
      // behaves exactly like null does on the line below it — it passes, and stays
      // findable by every other facet.
      // AN ALL-KINDS PAGE KEEPS THE LANDING PAGE'S CARAT RULE: a narrowed carat range holds weighed stones
      // only (its label says so and a note under the count names the cause), where this panel elsewhere
      // lets a stone with no weight through every carat range. Calibrated stock is sold by millimetre and
      // carries no carat; the Round page it replaces answered "1–2 ct" without it.
      if (ALL_KINDS && caratOn && !r.carat) return false;
      if (r.carat && (r.carat < st.carat[0] || r.carat > st.carat[1])) return false;
      if (r.l && (r.l < st.len[0] || r.l > st.len[1])) return false;
      if (r.w && (r.w < st.wid[0] || r.w > st.wid[1])) return false;
      // CAN BE SET — the shard's `st` column, which is "Ring", "Pendant" or
      // "Pendant+Ring". Ticking Ring must return every stone a ring builder can
      // take, INCLUDING the 3,418 that can also go in a pendant, so this is a
      // membership test against the split list and never a string equality: `===`
      // would answer "Ring" with only the 197 ring-ONLY stones and read, wrongly,
      // as almost no ring-settable diamonds in stock.
      if (except !== 'setting' && st.setting.length) {
        var mine = String(r.setting || '').split('+');
        var hit = false;
        for (var si = 0; si < st.setting.length; si++) {
          if (mine.indexOf(st.setting[si]) >= 0) { hit = true; break; }
        }
        if (!hit) return false;
      }
      if (except !== 'cutstyle'
          && st.cutstyle.length && st.cutstyle.indexOf(r.cutStyle) < 0) return false;
      // TREATMENT. ⚠️ A NULL HERE IS "NOT STATED", NOT "UNTREATED" — so an unlabelled
      // stone is excluded whenever the shopper asks for a specific value, including
      // "None". Letting nulls through a "None" filter would answer a disclosure
      // question with a shrug, which is the one thing this facet must not do.
      if (except !== 'treatment'
          && st.treatment.length && st.treatment.indexOf(r.treatment) < 0) return false;
      if (except !== 'quality'
          && st.quality.length && st.quality.indexOf(r.quality) < 0) return false;
      // Fancy colour is a different AXIS, never a worse grade — it is judged by its
      // own chips and never by the D-Z slider. Before this, an unlisted grade gave
      // indexOf === -1 and the colour guard was SKIPPED, so every fancy stone
      // passed at EVERY slider position and turned up inside ranges that exclude
      // it. Clarity and cut still apply: those are graded the same on a fancy stone.
      if (except === 'colour') {
        // the whole colour axis stands down — see the `except` note above
      } else if (r._fancy) {
        if (st.fancy.length) { if (st.fancy.indexOf(r._hue) < 0) return false; }
        else if (st.off.length || !colWideOpen) return false;
      } else if (r._off) {
        // OFF-SCALE (U-V, and whatever else arrives that is not on K-D). Same
        // treatment as fancy, and for the same reason: it is not a worse K, it is
        // not on that axis. Adding it to the ladder as a ninth rung — which this
        // briefly did for ONE stone of 7,991 — hands every shopper a rung they will
        // never want and implies we stock the L-through-T grades in between.
        if (st.off.length) { if (st.off.indexOf(r._offKey) < 0) return false; }
        else if (st.fancy.length || !colWideOpen) return false;
      } else {
        // Asking for a fancy hue or an off-scale grade is asking for those only.
        if (st.fancy.length || st.off.length) return false;
        if (r._col >= 0 && (r._col < st.col[0] || r._col >= st.col[1])) return false;
      }
      // NOT CLARITY-GRADED IS NOT "PASSES EVERY CLARITY". `Opaque` on a black diamond
      // is the correct descriptor for a stone the FL-I3 scale does not grade, and the
      // `>= 0` guard was simply SKIPPED on it — so 25 blacks answered every clarity
      // range there is. Same treatment fancy colour gets on the D-Z slider. They stay
      // findable: black diamonds are fancy colour (GIA grades them "Fancy black") and
      // `isFancy` already lists them under Fancy color -> Black.
      if (except !== 'clarity') {
        if (r._cla >= 0) {
          if (r._cla < st.cla[0] || r._cla >= st.cla[1]) return false;
        } else if (!claWideOpen) return false;
      }
      if (except !== 'cut'
          && r._cut != null && (r._cut < st.cut[0] || r._cut >= st.cut[1])) return false;
      return true;
    }
    var rows = all.filter(function (r) { return passes(r, null); });

    // The pool a facet's own counts are taken from: everything else applied, itself
    // stood down. Memoised — five facets over a shard of up to 1,605 rows is cheap,
    // but this runs on every filter click and there is no reason to redo it.
    var _pools = {};
    function poolFor(except) {
      if (!_pools[except]) {
        _pools[except] = all.filter(function (r) { return passes(r, except); });
      }
      return _pools[except];
    }

    /* ------------------------------------------------------------ sorting ---
       THE SAME NINE OPTIONS THE LANDING PAGES OFFER, under Shopify's own values,
       because the owner asked for parity and a shopper moving between the two
       pages should find the same menu doing the same thing.

       Four of them are facts about the stone and sort here. Three are rankings
       Shopify computes — Featured, Best selling and its duplicate Most relevant —
       and those are HARVESTED into shard columns by build_diamond_assets.py,
       because this grid is drawn from shards and never asks Shopify to order
       anything.

       ⚠️ "most-relevant" IS DELIBERATELY THE SAME COMPARATOR AS "best-selling".
       Measured on both /collections/sapphires and /collections/diamonds
       (2026-08-05): the two produce handle-for-handle identical pages. Shopify
       only ranks relevance against a search term and a collection page has none.
       Pointing it somewhere else to look useful would make this page disagree with
       every other one on the store. */
    function byRank(field) {
      // ⚠️ A NULL RANK IS "SHOPIFY DID NOT PLACE THIS STONE", NOT POSITION ZERO.
      // Treating it as 0 puts unranked stock FIRST, which is the loudest possible
      // way to be wrong; treating it as a large number silently invents a position.
      // They go to the END as a group, ordered by price so the tail is at least
      // stable and browsable rather than shuffling between loads.
      return function (a, b) {
        var x = a[field], y = b[field];
        if (x == null && y == null) return a.price - b.price;
        if (x == null) return 1;
        if (y == null) return -1;
        return x - y;
      };
    }
    // `localeCompare` with `numeric` so "2.01 ct …" sorts after "1.90 ct …" rather
    // than between "1.9" and "10" — every one of these titles opens with a decimal.
    function byTitle(dir) {
      return function (a, b) {
        return dir * String(a.title).localeCompare(String(b.title), 'en',
          { numeric: true, sensitivity: 'base' });
      };
    }
    function byCreated(dir) {
      return function (a, b) {
        var x = a.created, y = b.created;
        if (x == null && y == null) return a.price - b.price;
        if (x == null) return 1;
        if (y == null) return -1;
        return dir * (x - y);
      };
    }
    var SORTS = {
      'manual': byRank('featOrder'),
      'best-selling': byRank('bestOrder'),
      'most-relevant': byRank('bestOrder'),
      'title-ascending': byTitle(1),
      'title-descending': byTitle(-1),
      'price-ascending': function (a, b) { return a.price - b.price; },
      'price-descending': function (a, b) { return b.price - a.price; },
      'created-ascending': byCreated(1),
      'created-descending': byCreated(-1),
      // ⚠️ THE FOUR OLD KEYS ARE KEPT AS ALIASES, NOT DELETED. They are in every
      // link, bookmark and share made since this page shipped, and an unknown key
      // silently falls through to the default — so dropping them would quietly
      // re-sort a URL somebody had saved rather than 404 and say so.
      'price-asc': function (a, b) { return a.price - b.price; },
      'price-desc': function (a, b) { return b.price - a.price; },
      'carat-asc': function (a, b) { return (a.carat || 0) - (b.carat || 0); },
      'carat-desc': function (a, b) { return (b.carat || 0) - (a.carat || 0); }
    };
    // DEFAULT IS `manual` — "Featured" — which is what /collections/diamonds
    // serves with no `?sort_by`, so the page opens the way the collection is
    // merchandised rather than cheapest-first.
    // An unknown key falls back to the page's own default: Featured, or the collection's order on a scoped shape page.
    if (!SORTS[st.sort]) st.sort = SORTS[root.getAttribute('data-db-default-sort')] ? root.getAttribute('data-db-default-sort') : 'manual';
    rows.sort(SORTS[st.sort]);

    /* ------------------------------------------------------------- panel */
    panel.innerHTML = '';

    // KIND. Only on the all-stones page — the diamond page has no colored stones
    // to offer and a dead tab is worse than no tab. Rendered as `.sb-type` chips,
    // the same control the origin row uses, so the two read as one vocabulary.
    //
    // ⚠️ Every other selection is DROPPED when the kind changes, deliberately: a
    // clarity range or a fancy-hue pick means nothing on a sapphire, and carrying
    // them across would filter the new tab by a facet it does not display. Only
    // `origin` survives, because natural/lab applies to both.
    // 🚨 STONE AND FORMATION SHARE ONE LINE (owner, 2026-08-19: *"they are
    // occupying too much space as is when the row can be consolidated"*). Measured
    // before: two full-width `.sb__row`s, 1392px wide and 44px + 55px tall, each
    // carrying TWO controls — 99px of panel to ask two binary questions, above the
    // fold, on the page whose filters have to be near it. `.sb__cols` is the
    // existing pairing mechanism (Color|Clarity, Cut|Carat) and needs no new CSS:
    // `.sb__cols > .sb__row:only-child` already spans a lone survivor, which is
    // what keeps /collections/diamonds — where there is no Stone row at all —
    // rendering its Formation row full width exactly as it does today.
    var kindRow = null;
    if (BOTH_KINDS) {
      // 🚨 THE TWO TOP ROWS WERE COUNTED ON DIFFERENT BASES, and the mismatch was
      // visible on the page (owner, 2026-08-09): "Diamonds 2,189" sat above
      // "Natural 6,348 / Lab-Grown 7,426" — a subset advertised beside two totals,
      // so the numbers plainly did not add up.
      //
      // They now follow the ordinary facet rule: a row counts with its OWN facet
      // excluded and everything ABOVE it applied. Stone is the first row, so it
      // applies nothing and shows the true totals (7,878 / 5,896). Formation is the
      // second, so it applies Stone — pick Colored and Natural/Lab-Grown re-count
      // to the colored stones only, which is issue 3.
      var KTOT = { diamond: 0, color: 0 };
      // A scoped page counts ITS collection (the rank file's own split), not its shape or the catalogue.
      if (RANK) {
        KTOT.diamond = RANK.kinds.d;
        KTOT.color = RANK.kinds.c;
      } else Object.keys(META.kinds || {}).forEach(function (k) {
        KTOT.diamond += META.kinds[k].d || 0;
        KTOT.color += META.kinds[k].c || 0;
      });
      KTOT.all = KTOT.diamond + KTOT.color;
      // 🚨 A PHOTOGRAPH, NOT A PILL — and this is the one place that reverses the
      // rule stated on the Stone TYPE row below ("Diamonds/Colored and
      // Natural/Lab-Grown are POPULATIONS, not stone types, and there is no
      // photograph of 'Lab-Grown' to show"). That reasoning still holds for
      // FORMATION and formation keeps its pill. It does NOT hold here, because
      // both halves of THIS axis are things a shopper can be shown: a diamond and
      // a colored stone. Owner, 2026-08-19: *"I want to try and do away with the
      // pill ... A diamond for Diamonds and maybe a nice ruby for colored stones.
      // For the formation, if nothing can be done, the pills are ok"*.
      //
      // `.sb-gem` is stone-browse.css's, already loaded here and drawn by the
      // species row directly below — so the two rails line up column-for-column
      // and there is still exactly ONE tile treatment on the page.
      //
      // ⚠️ `is-current`, NOT `is-active`. This row is a SWITCHER (pressing it loads
      // the other tab's shards), like the species switcher on a landing page, and
      // `is-current` is the ink-filled "the page you are on" state. The species row
      // below is a multi-select filter and correctly uses `is-active`; one row
      // borrowing the other's state class is how the two start to look different.
      // The "?" (owner 2026-09-22, every shape page): what the switch changes. Gem search's two-tab row keeps none.
      // An EMPTY kind is not offered (slot 2, 2026-09-24): twelve shape pages hold one kind only (Trillion 268
      // colored, Octagon 59 diamonds), and "Diamonds 0" is a door to an empty grid. Every other row on the store
      // already drops a zero (Stone type, Color; the Shape row skips an empty page). The kind a shopper is ON stays.
      // The "?" says "Choose Diamonds ...", so it comes with the Diamonds choice: none on a colored-only page.
      kindRow = row('Stone', (ALL_KINDS && (KTOT.diamond > 0 || KIND === 'diamond')) ? 'stone-kind' : null,
        (ALL_KINDS ? [['all', 'All stones'], ['diamond', 'Diamonds'], ['color', 'Colored stones']]
                   : [['diamond', 'Diamonds'], ['color', 'Colored stones']]).filter(function (pair) {
          return pair[0] === 'all' || pair[0] === KIND || KTOT[pair[0]] > 0;
        }).map(function (pair) {
          var k = pair[0], on = k === KIND;
          var a = el('a', {
            class: 'sb-gem' + (on ? ' is-current' : ''), role: 'listitem',
            title: pair[1],
            href: resetHref(k === 'color' ? { origin: originParam(), gem: 'color' }
                            : (ALL_KINDS && k === 'diamond') ? { origin: originParam(), gem: 'diamond' }
                            : { origin: originParam() }),
            // A switch drops the other tab's filters on purpose (above): followed as written.
            'data-sr-reset': ''
          });
          var src = kindImg(k);
          if (src) {
            var box = el('span', { class: 'sb-gem__art' });
            var im = el('img', { src: src, alt: '', loading: 'lazy',
                                 width: '66', height: '66' });
            // A collection image can be replaced or removed in the admin between
            // page loads, and a broken <img> renders as a torn-page glyph inside
            // the plate — which reads as a bug. Falling back to the dot is the
            // documented no-photograph state. Same guard the species row carries.
            im.onerror = function () {
              box.removeChild(im);
              box.className = 'sb-gem__art sb-gem__art--dot sb-type__dot--' + (k === 'all' ? 'color' : k);
            };
            box.appendChild(im);
            a.appendChild(box);
          } else {
            // No sprite (any surface but this one): the population gradient, which
            // is the pill's own dot. The gradients live in stone-browse.css as
            // `--diamond`/`--color` modifiers — two inline copies of one palette is
            // the drift this repo keeps paying for.
            a.appendChild(el('span', {
              class: 'sb-gem__art sb-gem__art--dot sb-type__dot--' + (k === 'all' ? 'color' : k) }));
          }
          a.appendChild(el('span', { class: 'sb-gem__t', text: pair[1] }));
          a.appendChild(el('span', { class: 'sb-gem__n',
                                     text: KTOT[k].toLocaleString() }));
          if (on) a.setAttribute('aria-current', 'page');
          return a;
        }), 'sb__row--types');
    }

    // FORMATION. Natural and lab live in separate SHARDS, so every press is a page
    // load — these are links like every other chip in the panel. They are now
    // TOGGLES: press a selected one to remove it, and with none selected the grid
    // shows both. A sub-label used to say so, as Shape's "all shapes" did — both
    // are gone now (see below, and the Shape row's note of 2026-09-03) — on the
    // reasoning that an unfiltered row that looks identical to a filtered one is
    // how the old behaviour hid 5,091 lab-grown diamonds in plain sight.
    // ⚠️ NO SUB-LABEL. It read "natural and lab-grown" whenever nothing was
    // selected, on the reasoning that "an unfiltered row that looks identical to a
    // filtered one is how the old behaviour hid 5,091 lab-grown diamonds in plain
    // sight". That reasoning was written for SHAPE, where the row holds nineteen
    // tiles and "all shapes" genuinely tells you something you cannot see. Here the
    // row is TWO chips, both spelled out, both carrying their count, and neither
    // ink-filled — the unfiltered state is fully legible from the chips themselves,
    // so the line only re-read them. Owner, 2026-08-19: *"The subheading under
    // formation 'natural and lab-grown' is redundant too, that can be removed"*.
    // It also made this row 55px against Stone's 44px, which is the height the two
    // now have to share.
    // On an all-kinds page the three formations are counted from the loaded stones (one shape, both
    // shard sets), with the formation facet itself excluded — the ordinary facet rule. A formation with no
    // stones in the current tab is not offered (Cultured on the Diamonds tab), unless it is selected.
    var formN = null;
    if (ALL_KINDS) {
      formN = { nat: 0, lab: 0, cult: 0 };
      poolFor('origin').forEach(function (r) { formN[formOf(r)] += 1; });
    }
    var formationRow = row(BOTH_KINDS ? 'Formation' : 'Diamond type', 'formation',
      FORMS.filter(function (o) { return !formN || formN[o] > 0 || originSel.indexOf(o) >= 0; })
        .map(function (o) {
      // Counted against the SELECTED KIND — see the note on the Stone row. On the
      // diamond page BOTH_KINDS is false and `kinds` still splits every shard, so
      // this reads the diamond half and matches what that page has always shown.
      var n = formN ? formN[o] : Object.keys(META.shards).reduce(function (t, k) {
        if (k.split('|')[1] !== o) return t;
        if (!BOTH_KINDS) return t + META.shards[k].n;
        var kd = (META.kinds || {})[k] || {};
        return t + (KIND === 'diamond' ? (kd.d || 0) : (kd.c || 0));
      }, 0);
      var on = originSel.indexOf(o) >= 0;
      // MULTI-SELECT, EVERY CHIP A TOGGLE — the same rule as the shape tiles below.
      // Dropping the last one leaves no `origin` param at all, which is how "both"
      // is expressed: absence, not a sentinel value.
      var next = on ? originSel.filter(function (y) { return y !== o; })
                    : originSel.concat([o]);
      var a = el('a', {
        class: 'sb-type' + (on ? ' is-current' : ''), role: 'listitem',
        'aria-pressed': String(on),
        title: FORM_LABEL[o]
              + (on ? ' — selected, press to remove' : ''),
        // 🚨 `hrefWith`, NOT `resetHref` — AND THAT CHANGED THE DAY FORMATION STOPPED
        // BEING A POPULATION SWITCH. While it picked which shard set existed, wiping
        // the other facets was right: a clarity range measured against one population
        // means nothing against another. It is now an ordinary filter over one pool,
        // and resetting still cost the shopper everything they had set — including
        // the `pmax` they arrived with. Landing from "See all 6,110 diamonds in this
        // budget" and pressing Natural silently dropped the budget, which is the very
        // promise that link exists to keep.
        //
        // ⚠️ The grade ladders do NOT differ across formation — a lab diamond is
        // graded D–Z and FL–I3 exactly like a natural one — so there is nothing here
        // that a reset was protecting. The STONE row above still resets, because
        // diamond -> colored genuinely swaps the whole rail.
        href: hrefWith({ origin: next.join(',') || null })
      }, [
        // Modifier class, not an inline gradient — see the Stone row above.
        // `nat`/`lab` are the same two names the builder's Formation row uses.
        // Cultured has no palette class; it takes the pearl swatch from the configured gem_map.
        o === 'cult'
          ? el('span', { class: 'sb-type__dot',
                         style: 'background:' + (gemDot('Pearl') || 'linear-gradient(145deg,#f4ece0,#cdbba4)') + ';' })
          : el('span', { class: 'sb-type__dot sb-type__dot--' + (o === 'lab' ? 'lab' : 'nat') }),
        // "Lab-Grown", capital G — owner rule. It is a compound proper term here,
        // and it must match `custom.stone_origin`, whose value is "Lab-Grown".
        el('span', { class: 'sb-type__t', text: FORM_LABEL[o] }),
        el('span', { class: 'sb-type__n', text: n.toLocaleString() })
      ]);
      return a;
    }), 'sb__row--types');

    // The pair. With no Stone row (the diamond page) Formation is appended alone
    // and `.sb__cols > .sb__row:only-child` spans it across both columns, so that
    // page is byte-for-byte unchanged.
    if (ALL_KINDS) panel.appendChild(kindRow);   // Formation is placed beside Origin further down
    else panel.appendChild(kindRow
      ? el('div', { class: 'sb__cols' }, [kindRow, formationRow])
      : formationRow);

    // SHAPE. Counts come from the shard registry, so a shape we hold none of in
    // this origin reads 0 rather than silently vanishing.
    // 🚨 A SHAPE'S COUNT IS PER KIND, NOT PER SHARD. `META.shards[k].n` is the whole
    // shard — both kinds — so the DIAMOND tab advertised "Pearl 149", "Briolette 15"
    // and "Cabochon 3". A pearl is never a diamond and a diamond is never cut en
    // cabochon; clicking any of them returned an empty grid from a tile that had
    // just promised results. Same wrong-base error as the two rows above it.
    //
    // On /collections/diamonds BOTH_KINDS is false and the whole shard IS the
    // answer, so that page keeps reading `.n` and is untouched.
    // Summed across every SELECTED formation, or the tile would advertise the
    // natural half of a grid showing both — the same wrong-base error the two
    // paragraphs above describe, one axis over.
    function shapeN(sh) {
      return origins.reduce(function (t, o) {
        var k = sh + '|' + o;
        if (!BOTH_KINDS) return t + ((META.shards[k] || {}).n || 0);
        var kd = (META.kinds || {})[k] || {};
        return t + ((KIND === 'all' ? (kd.d || 0) + (kd.c || 0) : KIND === 'diamond' ? kd.d : kd.c) || 0);
      }, 0);
    }
    // A shape with none of the CURRENT kind is not offered — it is not that we hold
    // none in stock, it is that the category does not exist for this kind.
    var shapes = shapesForOrigin.filter(function (x) { return shapeN(x) > 0; })
      .sort(function (a, b) { return shapeN(b) - shapeN(a); });
    // 🚨 A SHAPE WITH NO SILHOUETTE IS NOT A SHAPE A SHOPPER RECOGNISES.
    // The sprite is rendered from the section's `shape_map`; anything absent from
    // it drew a label with an empty box above it, which reads as a broken tile.
    // Owner, 2026-08-09: "some shapes don't make sense — the ones that don't have a
    // silhouette."
    //
    // ⚠️ THE STONES ARE NOT LOST. Shape is one facet; a stone filed under a shape
    // with no tile is still reachable by species, color, carat and price, and still
    // appears in the grid whenever no shape is ticked. Today that is OTHER and
    // UNUSUAL — 4 stones of 13,774 that carry no meaningful shape at all. The
    // marker below is so a rail that quietly shrinks can be diagnosed from the
    // served HTML rather than guessed at.
    var dropped = shapes.filter(function (x) { return !shapeIcon(x); });
    if (dropped.length) {
      grid.insertAdjacentHTML('beforebegin',
        '<!-- DB_SHAPE_NO_SILHOUETTE ' + dropped.join(',') + ' -->');
    }
    shapes = shapes.filter(function (x) { return !!shapeIcon(x); });
    // Captured so the colored rows can be inserted ABOVE it — see COLORED_ROWS.
    var COLORED_ROWS = [];
    // NO HELP KEY, AND NO "all shapes" SUB-LINE (owner decision 2026-09-03, taken
    // knowingly): that sub-line reported STATE ("nothing is ticked"), not the
    // meaning of the facet, and the help panels explain meaning. Restoring it is a
    // one-line change — the old form was `picked.length ? null : 'all shapes'`
    // rendered as an `<em>` under the label, which `.sb__label em` still styles.
    // 🚨 ON A SCOPED SHAPE PAGE THE SHAPE ROW IS THE SWITCHER, NOT A FILTER — the landing page's rule
    // (stone-browse.liquid "IS THIS A SHAPE PAGE?"): the page holds ONE shape, so a filter here would be a
    // dead one-value row and a one-way door. The tiles are the section's roster, with each shape page's live
    // count, the current page `is-current` — the same markup the landing page renders.
    var shapeRowEl = (SCOPE_SHAPE && SHAPE_LINKS.length) ? row('Shape', null,
      SHAPE_LINKS.map(function (L) {
        var key = String(L.s || '').toUpperCase(), cur = key === SCOPE_SHAPE;
        // THE SAME NIGHT'S NUMBER AS THE REST OF THE PAGE (owner 2026-09-22). L.n is the collection's LIVE count, which
        // ran ahead of the shards between the 03:00 creations and the push (Round 5,415 beside a Stone row of 4,663);
        // the rank file carries every roster collection's count from the build that made these shards, so it wins.
        var nightly = RANK && RANK.counts ? RANK.counts[String(L.u || '').split('/').pop()] : null;
        var n = typeof nightly === 'number' ? nightly : Number(L.n || 0);
        var a = el('a', { class: 'sb-shape' + (cur ? ' is-current' : ''), role: 'listitem', href: L.u,
                          title: L.t });
        if (cur) a.setAttribute('aria-current', 'page');
        var art = el('span', { class: 'sb-shape__art' });
        var ic = shapeIcon(key);
        if (ic) art.appendChild(ic);
        a.appendChild(art);
        a.appendChild(el('span', { class: 'sb-shape__t', text: L.t }));
        a.appendChild(el('span', { class: 'sb-shape__n', text: n.toLocaleString() }));
        return a;
      }), 'sb__row--lead') : row('Shape', null,
      shapes.map(function (x) {
      var n = shapeN(x);
      var on = picked.indexOf(x) >= 0;
      // MULTI-SELECT, AND EVERY TILE IS A TOGGLE. Pressing a selected shape
      // REMOVES it — the previous build made each tile an absolute "shape=X" link,
      // so a chosen shape could only ever be swapped for another and never undone.
      // Dropping the last one leaves no `shape` param at all, which is how "all
      // shapes" is expressed: absence, not a sentinel value.
      var next = on ? picked.filter(function (y) { return y !== x; })
                    : picked.concat([x]);

      var a = el('a', {
        // is-active — see the .sb-colour note above; only .sb-type uses is-current.
        class: 'sb-shape' + (on ? ' is-active' : ''), role: 'listitem',
        'aria-pressed': String(on),
        title: titleCase(x)
              + (on ? ' — selected, press to remove' : ''),
        // 🚨 `hrefWith`, NOT a fresh URLSearchParams. This was
        // `'?' + new URLSearchParams({origin, shape})`, which dropped every param it
        // did not name — so on the all-stones tool EVERY shape click lost
        // `view=stone-search` and fell back to the collection's real template, the
        // gemstone hub. Deselecting the last shape produced a bare `?`, landing on
        // the hub with nothing selected at all. Shape is one facet among many and
        // must PRESERVE the rest, which is exactly what hrefWith does.
        href: hrefWith({ shape: next.length ? next.join(',') : null })
      });
      var art = el('span', { class: 'sb-shape__art' });
      var ic = shapeIcon(x);
      if (ic) art.appendChild(ic);
      a.appendChild(art);
      a.appendChild(el('span', { class: 'sb-shape__t', text: titleCase(x) }));
      a.appendChild(el('span', { class: 'sb-shape__n', text: n.toLocaleString() }));
      return a;
    }));
    panel.appendChild(shapeRowEl);

    // FANCY COLOUR — only where there is fancy stock, counted against every OTHER
    // facet (`poolFor('colour')`) so the numbers answer "how many, given what else is
    // ticked" and no empty row is ever drawn.
    var hues = {};
    poolFor('colour').forEach(function (r) {
      if (r._fancy && r._hue) hues[r._hue] = (hues[r._hue] || 0) + 1;
    });
    var hueKeys = Object.keys(hues).sort(function (a, b) { return hues[b] - hues[a]; });
    var fancyRow = null;
    if (hueKeys.length) {
      fancyRow = row('Fancy color', 'fancy-color',
        hueKeys.map(function (h) {
          var on = st.fancy.indexOf(h) >= 0;
          var next = on ? st.fancy.filter(function (x) { return x !== h; })
                        : st.fancy.concat([h]);
          return el('a', {
            // `is-active`, NOT `is-current` — stone-browse.css uses is-current only
            // on .sb-type; .sb-colour and .sb-shape both key on is-active.
            class: 'sb-colour sb-colour--' + h.toLowerCase() + (on ? ' is-active' : ''),
            role: 'listitem', title: h,
            // 🚨 `hrefWith`, NOT a fresh URLSearchParams — THE THIRD TIME THIS EXACT
            // LINE HAS BEEN WRITTEN AND THE SECOND TIME IT HAS BEEN REPORTED. The
            // Stone/Formation rows were fixed 2026-08-09 and the shape row on the
            // same day (see both notes above); these two swatch rows were left on
            // `'?' + new URLSearchParams({origin, shape, fancy})`, which mints a URL
            // from THREE named params and throws away everything else — `view`
            // first of all. Owner, 2026-08-17: pressing a colour on
            // `/collections/all-gemstones?view=stone-search` landed on
            // `?origin=null&fancy=Green`, i.e. the collection's own template with no
            // browse tool on it at all. It also dropped `gem`, `sort` and all ten
            // sliders, so a shopper three facets deep lost the lot for one click.
            //
            // ⚠️ AND `origin=null` IS THE LITERAL FOUR-CHARACTER STRING. Since
            // formation became "none means all", `originParam()` returns null for
            // the common case — and URLSearchParams STRINGIFIES it rather than
            // dropping it. `patched()` deletes a null key instead, which is why the
            // shape row never grew this. Nothing errors: `originSel` filters "null"
            // against ORIGINS, gets [], and shows everything — the right stones
            // behind a URL that reads like a bug and cannot be shared.
            //
            // Restating `origin`/`shape` is unnecessary as well as harmful: they are
            // already in `location.search`, so a chip patches ONLY its own key.
            // Clearing the D-Z slider is the one thing worth keeping from the old
            // form — a hue and a colourless range ask opposite questions, and
            // `passes()` ignores the slider once `st.fancy` is non-empty, so leaving
            // it set renders a Color chip that is on and doing nothing.
            href: hrefWith({ fancy: next.join(',') || null,
                             colmin: null, colmax: null })
          }, [
            el('span', { class: 'sb-colour__sw',
                         style: '--sw: ' + (HUE_SW[h] || HUE_SW.Gray) + ';' }),
            el('span', { class: 'sb-colour__t', text: h }),
            el('span', { class: 'sb-colour__n', text: String(hues[h]) })
          ]);
        }));
    }

    // OUTLIERS GET A ROW, NOT A RUNG. Any colour grade off the K-D ladder that is
    // not fancy — "U-V" on a single stone today — is listed here instead of being
    // wedged into the slider. That keeps the eight grades people actually shop by
    // spread across the whole track, and still leaves the odd stone findable rather
    // than stranded. Counted off the pool, so the row simply does not exist on a
    // shape or origin that holds none, and a grade nobody stocks never appears.
    var offs = {};
    poolFor('colour').forEach(function (r) {
      if (r._off) offs[r._offKey] = (offs[r._offKey] || 0) + 1;
    });
    var offKeys = Object.keys(offs).sort();
    var otherRow = null;
    if (offKeys.length) {
      otherRow = row('Other color', 'other-color',
        offKeys.map(function (g) {
          var on = st.off.indexOf(g) >= 0;
          var next = on ? st.off.filter(function (x) { return x !== g; })
                        : st.off.concat([g]);
          return el('a', {
            class: 'sb-colour' + (on ? ' is-active' : ''), role: 'listitem',
            title: g,
            // Same fix as the Fancy color row above, same reasoning — and it had to
            // be made twice because the two rows were written as a pair. ⚠️ The
            // param stays `offcolor`: `passes()` reads `st.off` separately from
            // `st.fancy` here (the BUILDER folds both into one `fancy` list), and
            // renaming it would break every link already in the wild.
            href: hrefWith({ offcolor: next.join(',') || null,
                             colmin: null, colmax: null })
          }, [
            el('span', { class: 'sb-colour__sw',
                         style: '--sw: linear-gradient(145deg,#f2ead6,#d8c79a);' }),
            el('span', { class: 'sb-colour__t', text: g }),
            el('span', { class: 'sb-colour__n', text: String(offs[g]) })
          ]);
        }));
    }
    // PAIRED, like Color|Clarity. Both are narrow chip rows and each on its own
    // full-width line left most of the panel empty. `.sb__cols > .sb__row:only-child`
    // spans whichever one survives when the other has no stock.
    // Fancy/other color are D-Z-scale concepts. A sapphire is not "warmer than K".
    if (KIND === 'diamond' && (fancyRow || otherRow)) {
      panel.appendChild(el('div', { class: 'sb__cols' },
        [fancyRow, otherRow].filter(Boolean)));
    }

    // ⚠️ RANGED ROWS GO TWO TO A LINE, in `.sb__cols`. One slider per full-width row
    // is what the first cut shipped and it pushed the results grid most of a screen
    // down while stretching every track edge to edge. The stone landing panel pairs
    // Carat with Price for exactly this reason; these pair by kind — the two
    // letter-graded 4Cs, then cut with carat. stone-browse.css already spans a lone
    // survivor across both columns (`.sb__cols > .sb__row:only-child`), which is
    // what Price relies on below.
    function cols(a, b) {
      return el('div', { class: 'sb__cols' }, b ? [a, b] : [a]);
    }
    // 🚨 THE FOUR Cs ARE A DIAMOND VOCABULARY AND DO NOT TRANSFER. D-Z color,
    // VVS-I clarity and the cut ladder describe a colorless stone's deviation from
    // colorless — none of it says anything about a ruby. Measured on the 5,896
    // colored stones in this shard set: clarity is null on ALL of them, and
    // `colour_grade` is 76% empty and holds two-letter supplier codes.
    // Rendering these rows on the colored tab would draw three sliders that filter
    // nothing, which reads as a broken panel rather than an inapplicable one.
    if (KIND === 'diamond') {
      panel.appendChild(cols(
        scaleRow('Color', 'color-scale', COLOR, st.col[0], st.col[1],
          // ⚠️ BOTH off-ladder params, not just `fancy` — the reciprocal of the two
          // swatch rows above, and the same one-group contract. `passes()` drops
          // every fancy stone the moment `st.off` is non-empty, so a stale
          // `offcolor` riding through a slider move emptied the Fancy color row
          // while its own chips still read as selected.
          function (a, b) { apply({ colmin: a, colmax: b, fancy: null, offcolor: null }); },
          null, true),
        scaleRow('Clarity', 'clarity', CLARITY, st.cla[0], st.cla[1],
          function (a, b) { apply({ clmin: a, clmax: b }); }, null, true)));
    } else {
      // 🚨 SPECIES IS A PHOTOGRAPH AND COLOR IS A SWATCH — the same two instruments
      // the species landing pages use, and for the same reason jewelry-browse.liquid
      // gives: a sapphire IS its blue, so a word alone throws away the one thing the
      // shopper is choosing on. Owner, 2026-08-09: "Stone Type should look exactly
      // the way it looks when you land on the sapphire, ruby collection, showing the
      // image of the stones", and "colors should have swatches".
      //
      // `.sb-gem` / `.sb-colour` are stone-browse.css's and stone-list.css's own
      // classes, both already loaded by this section — emitting them means one stone
      // reads identically here and on /collections/sapphires, and it is why the
      // color palette works with no color values in this file at all: the
      // `sb-colour--<key>` class carries it, keyed on the value write_gem_color.py
      // writes.
      function visualRow(label, help, field, param, applied, art, order) {
        var counts = {};
        poolFor(param).forEach(function (r) {
          var v = r[field];
          if (v) counts[v] = (counts[v] || 0) + 1;
        });
        var keys;
        if (order === 'alpha') {
          // The landing page's order (Shopify lists filter values A-Z), kept on an all-kinds shape page.
          keys = Object.keys(counts).sort(function (a, b) { return a.localeCompare(b); });
        } else if (order && order.length) {
          // The configured order first, then anything unmapped by count — so a new
          // species appears rather than being silently dropped for want of a line.
          keys = order.filter(function (k) { return counts[k]; })
            .concat(Object.keys(counts).filter(function (k) { return order.indexOf(k) < 0; })
              .sort(function (a, b) { return counts[b] - counts[a]; }));
        } else {
          keys = Object.keys(counts).sort(function (a, b) {
            return counts[b] - counts[a] || a.localeCompare(b);
          });
        }
        if (!keys.length) return null;
        return row(label, help, keys.map(function (k) {
          var on = applied.indexOf(k) >= 0;
          var next = on ? applied.filter(function (x) { return x !== k; })
                        : applied.concat([k]);
          var patch = {};
          patch[param] = next.length ? next.join(',') : null;
          return art(k, on, counts[k], hrefWith(patch));
        }), 'sb__row--types');
      }

      // 🚨 `.sb-gem` — THE PHOTOGRAPH, NOT THE PILL (owner, 2026-08-19: *"Stone type
      // filters throughout the site should not be pills, they should show the
      // gems"*). This REVERSES the note that used to sit here, and the reversal is
      // deliberate rather than a regression: the old note argued for the pill on the
      // ground that /collections/sapphires drew one, which was a correct consistency
      // argument anchored on the surface the owner has now changed. Both moved
      // together — stone-browse.liquid, this row and the builders' step 2 all draw
      // `.sb-gem` off `stone-browse.css`, so there is still exactly one treatment.
      // ⚠️ Do NOT "restore" it here alone; a single pill in a row of photographs is
      // the two-vocabularies state this move exists to end.
      //
      // The two rows ABOVE this one keep `.sb-type`, and that is not an oversight:
      // Diamonds/Colored and Natural/Lab-Grown are POPULATIONS, not stone types, and
      // there is no photograph of "Lab-Grown" to show.
      //
      // `.sb-gem` also keeps the "Sapphire813" fix the old note credits to `.sb-type`
      // — the name and count are separate BLOCK children there, so they cannot run
      // together whatever the flex gap does.
      var spRow = visualRow('Stone type', null, 'species', 'sp', st.sp,
        function (k, on, n, href) {
          // `is-active` (not `is-current`) — this is a multi-select FILTER; the
          // landing pages' `is-current` means "the page you are on", and one row
          // using the other's state class is how the two start to look different.
          var a = el('a', { class: 'sb-gem' + (on ? ' is-active' : ''), href: href,
            role: 'listitem', 'aria-pressed': String(on),
            title: k });
          // On the all tab Diamond is a stone type; gem_map must not gain a Diamond line (it would put a
          // Diamond tile on every species page), so it borrows the Diamonds tab's photograph.
          var src = gemImg(k) || (KIND === 'all' && k === 'Diamond' ? kindImg('diamond') : null);
          if (src) {
            var box = el('span', { class: 'sb-gem__art' });
            var im = el('img', { src: src, alt: '', loading: 'lazy',
                                 width: '66', height: '66' });
            // A collection image can be replaced or removed in the admin between page
            // loads. A broken <img> renders as a torn-page glyph inside the plate,
            // which reads as a bug; falling back to the species swatch reads as the
            // documented no-photograph state.
            im.onerror = function () {
              box.removeChild(im);
              box.className = 'sb-gem__art sb-gem__art--swatch';
              box.style.setProperty('--gem',
                gemDot(k) || 'linear-gradient(145deg,#d8cfc4,#a89e90)');
            };
            box.appendChild(im);
            a.appendChild(box);
          } else {
            // A species with no map line still filters — it gets the neutral swatch
            // and sorts to the end, rather than vanishing from a row it belongs in.
            a.appendChild(el('span', { class: 'sb-gem__art sb-gem__art--swatch',
              style: '--gem: ' + (gemDot(k) || 'linear-gradient(145deg,#d8cfc4,#a89e90)') + ';' }));
          }
          a.appendChild(el('span', { class: 'sb-gem__t', text: k }));
          a.appendChild(el('span', { class: 'sb-gem__n', text: n.toLocaleString() }));
          return a;
        }, ALL_KINDS ? 'alpha' : GEM_ORDER);

      var hueRow = visualRow('Color', 'gem-color', 'hue', 'hue', st.hue,
        function (k, on, n, href) {
          // The class is the palette; no inline colour is emitted, exactly as
          // stone-browse.liquid does it. A hue with no palette entry falls back to
          // the sheet's own default rather than to a grey blob written here.
          var key = String(k).toLowerCase().replace(/\s+/g, '-');
          var a = el('a', {
            class: 'sb-colour sb-colour--' + key + (on ? ' is-active' : ''),
            href: href, role: 'listitem', 'aria-pressed': String(on),
            title: k });
          a.appendChild(el('span', { class: 'sb-colour__sw' }));
          a.appendChild(el('span', { class: 'sb-colour__t', text: k }));
          a.appendChild(el('span', { class: 'sb-colour__n', text: n.toLocaleString() }));
          return a;
        }, ALL_KINDS ? 'alpha' : null);
      // ⚠️ APPENDED HERE ONLY AS A FALLBACK. On the colored tab both rows are moved
      // ABOVE Shape (see COLORED_ROWS below) — species is the primary decision on a
      // colored stone, the way the four Cs are on a diamond, and it belongs where a
      // shopper looks first.
      // Species first, then color, both ABOVE Shape (owner: "it should be higher
      // up"). insertBefore rather than reordering the build, because every row's
      // COUNTS depend on `poolFor`, which depends on the full state — computing
      // them earlier would mean computing them twice.
      COLORED_ROWS = [spRow, hueRow].filter(Boolean);
      COLORED_ROWS.forEach(function (r) { panel.insertBefore(r, shapeRowEl); });
    }
    // SHAPE LEADS ON A SCOPED PAGE, as it does on the landing page it replaces (owner 2026-08-19: "bring up
    // the shape to the top"): moved once every other row that is placed relative to it is in.
    if (SCOPE_SHAPE && SHAPE_LINKS.length && shapeRowEl.parentNode === panel) {
      panel.insertBefore(shapeRowEl, panel.firstChild);
    }

    // Carat and price are continuous, so their slider indexes 100 steps across the
    // real range and prints the value rather than a ladder label.
    // 🚨 A LINEAR SLIDER OVER A 1,586x RANGE IS NOT A CONTROL, IT IS A CLIFF.
    // Measured 2026-08-10 on the diamond tab: price runs $125–$198,275, so a
    // 100-step linear scale was $1,982 a step and **88.7% of the catalogue sat
    // inside the FIRST step**. The median diamond ($505) landed at position 0.2 of
    // 100 and half the track bought nothing under $99,000. Carat has the same shape,
    // gentler — the median 1.03 ct sat at 6.2 of 100.
    //
    // LOG, and 1,000 steps. Travel becomes proportional to RATIO rather than to
    // dollars, which is what a shopper means by "about twice as much": the median
    // moves to 19% of the track, the 90th percentile to 41%, the 99th to 71%. It is
    // also the mapping the homepage budget slider has always used, so the two
    // controls on this store now feel the same and round the same.
    // 🚨 THE SCALE IS `SRScale`, THE STORE'S ONE MAPPING — see assets/sr-scale.js for
    // the measurements and the four rules. It is loaded ahead of this file by
    // sections/diamond-search.liquid and resolved by `RB.contRow` (the engine,
    // assets/ring-builder.js) at the moment the row is built — the ONE place on the
    // store that maps a slider position to a value (verify_slider_scales.py). There
    // is no local fallback on purpose: a silent linear fallback is exactly the
    // 88.7%-in-one-step bug this replaced, and it would be invisible.
    // This wrapper keeps the panel's URL contract: the engine hands back two raw
    // values and THIS is where they become `key[0]` / `key[1]` in the address bar.
    function contRow(label, help, lo, hi, min, max, key, fmt) {
      return RB.contRow(label, help, lo, hi, min, max,
        function (a, b) {
          var patch = {};
          patch[key[0]] = fmt.raw(a);
          patch[key[1]] = fmt.raw(b);
          apply(patch);
        }, fmt);
    }
    var caratRow = contRow('Carat', null, st.carat[0], st.carat[1], cMin, cMax,
      ['ctmin', 'ctmax'],
      { raw: function (v) { return v.toFixed(2); },
        show: function (v) { return v.toFixed(2) + ' ct'; } });
    // The landing page's own qualifier, drawn the way Origin's "where known" is (see `passes`).
    if (ALL_KINDS) {
      var clab = caratRow.querySelector('.sb__label');
      if (clab) clab.appendChild(el('em', { text: 'weighed stones only' }));
    }
    if (KIND === 'diamond') {
      panel.appendChild(cols(
        scaleRow('Cut', 'cut-grade', CUT, st.cut[0], st.cut[1],
          function (a, b) { apply({ cutmin: a, cutmax: b }); }, null, true),
        caratRow));
    } else {
      // ⚠️ CARAT IS PAIRED WITH PRICE HERE, NOT LEFT ALONE, because 32% of the
      // colored cohort is CALIBRATED stock sold by millimetre and carries no carat
      // at all. Those stones filter by Length/Width in the dropdown row below. A
      // lone full-width carat slider would imply it is the primary size control
      // for a tab where a third of the stones ignore it.
      panel.appendChild(cols(caratRow,
        contRow('Price', null, st.price[0], st.price[1], pMin, pMax,
          ['pmin', 'pmax'],
          { raw: function (v) { return Math.round(v); },
            show: function (v) { return money(Math.round(v)); } })));
    }
    // Price last and alone — the widest range on the panel, and the one facet a
    // shopper is most likely to set first. `:only-child` spans it across both
    // columns, which is stone-browse.css's own handling of an unpaired row.
    if (KIND === 'diamond') {
      panel.appendChild(cols(
        contRow('Price', null, st.price[0], st.price[1], pMin, pMax,
          ['pmin', 'pmax'],
          { raw: function (v) { return Math.round(v); },
            show: function (v) { return money(Math.round(v)); } })));
    }
    // ALL-KINDS PAGE: FORMATION | ORIGIN, the landing page's own pairing and place (below Carat | Price).
    // Origin is a CHIP ROW there, as on the landing page ("Origin where known", A-Z) — not the dropdown the
    // colored tab of gem search uses; the Diamonds tab has no origins, so Formation stands alone on it.
    if (ALL_KINDS) {
      var coRowN = {};
      poolFor('country').forEach(function (r) { if (r.country) coRowN[r.country] = (coRowN[r.country] || 0) + 1; });
      var coRowKeys = Object.keys(coRowN).sort(function (a, b) { return a.localeCompare(b); });
      var originRow = null;
      if (KIND !== 'diamond' && coRowKeys.length) {
        originRow = row('Origin', 'origin', coRowKeys.map(function (k) {
          var on = st.country.indexOf(k) >= 0;
          var next = on ? st.country.filter(function (y) { return y !== k; }) : st.country.concat([k]);
          return el('a', { class: 'sb-chip' + (on ? ' is-active' : ''), role: 'listitem',
                           'aria-pressed': String(on), href: hrefWith({ country: next.join(',') || null }) },
            [document.createTextNode(k), el('span', { class: 'sb-chip__n', text: coRowN[k].toLocaleString() })]);
        }));
        var olab = originRow.querySelector('.sb__label');
        if (olab) olab.appendChild(el('em', { text: 'where known' }));
      }
      panel.appendChild(originRow ? cols(formationRow, originRow) : cols(formationRow));
    }

    /* ------------------------------------------------- the refinement row ---
       Can be set · Cut style · Length · Width, as dropdowns, in the landing pages'
       own order and markup (`.sb__dds` > `.sb-dd`). Keep the PRIMARY axes out of
       here — type, shape, the four Cs, carat and price are decisions, and these are
       refinements. That is the same line sections/stone-browse.liquid draws.

       ⚠️ TREATMENT IS DELIBERATELY ABSENT, and it is a measurement rather than an
       oversight. Stuller documents it on 80.1% of these stones, but the values are
       `HP` 6,844 / `N` 399 / `HPHT` 1 — 94% one value — and on a catalogue that is
       75% lab-grown the distinction is close to meaningless to a shopper. It would
       ship as a near-constant chip. It needs copy before it needs a filter. */
    var dds = el('div', { class: 'sb__dds' });

    // CAN BE SET. 🚨 THIS IS `custom.setting_type` (the shard's `st`), NOT
    // `custom.wearability`. Wiring it to `wear` builds a chip row that cannot
    // narrow anything: wearability is 100% filled here with ONE value on all 7,890
    // diamonds. It is printed once above the list instead, where a constant belongs.
    //
    // Counted off the pool like the fancy and off-scale rows, so a value we hold
    // none of in this shard set simply has no chip and the counts are always true.
    // A stone in NO builder cohort gets no chip of its own: "settable in neither"
    // is not something a shopper filters FOR, and it is already the complement of
    // ticking both.
    var setN = {};
    poolFor('setting').forEach(function (r) {
      String(r.setting || '').split('+').filter(Boolean).forEach(function (v) {
        setN[v] = (setN[v] || 0) + 1;
      });
    });
    // Ring, Pendant, Earring — write_setting_type.py's own order (it appends in
    // that sequence), so the chips read the same way on both pages. Anything else
    // the rule starts emitting is appended rather than dropped: an exclusion, not
    // an inclusion, exactly like the cut-style skip list below.
    var SET_ORDER = ['Ring', 'Pendant', 'Earring'];
    var setKeys = SET_ORDER.filter(function (k) { return setN[k]; })
      .concat(Object.keys(setN).filter(function (k) { return SET_ORDER.indexOf(k) < 0; }));
    // TREATMENT IS LEFTMOST — owner rule 2026-08-02, the same position it holds on
    // the coloured-stone panel, so the two pages read alike.
    //
    // ⚠️ COLOUR ENHANCEMENT ONLY, AND "where known" IS LITERAL. The shard leaves
    // `tr` null wherever the origin of a stone's colour is not stated (a fancy hue
    // Stuller has not flagged), so this row NEVER accounts for the whole pool and
    // the counts will not sum to the total. That is honest rather than broken: see
    // build_diamond_assets.py, which explains why Stuller's own `treatment` field
    // cannot be used here (its `HP` is the lab-grown GROWTH METHOD on 6,703 stones,
    // not a treatment, and a facet reading it would brand the lab catalogue).
    var trN = {};
    poolFor('treatment').forEach(function (r) {
      if (r.treatment) trN[r.treatment] = (trN[r.treatment] || 0) + 1;
    });
    // "None" first, then the disclosures. Frequency order would bury a 7-stone
    // enhanced hue under a 7,000-stone "None" on some shapes and float it on
    // others, so the row would reorder itself as the shopper changed shape.
    var TR_ORDER = ['None', 'Color Enhanced'];
    var trKeys = TR_ORDER.filter(function (k) { return trN[k]; })
      .concat(Object.keys(trN).filter(function (k) { return TR_ORDER.indexOf(k) < 0; }));
    // ORIGIN — colored tab only. A diamond's country is not a thing shoppers ask
    // for and Stuller does not supply it; on a ruby it is the single biggest price
    // driver after size. `custom.origin`, normalised through country_names.py, so
    // "Burma" stays "Burma" (a trade term of art carrying a real premium) and is
    // never auto-corrected to Myanmar.
    var ddOrigin = null;
    if (KIND !== 'diamond' && !ALL_KINDS) {   // an all-kinds page shows Origin as its chip row instead
      var coN = {};
      poolFor('country').forEach(function (r) {
        if (r.country) coN[r.country] = (coN[r.country] || 0) + 1;
      });
      // Frequency order, unlike Treatment's fixed order: there is no "None first"
      // to preserve and a shopper scanning for a source wants the ones we actually
      // hold at the top. ~30 countries, so the dropdown carries them all.
      var coKeys = Object.keys(coN).sort(function (a, b) {
        return coN[b] - coN[a] || a.localeCompare(b);
      });
      ddOrigin = chipsDropdown('Origin', 'where it was mined',
        coKeys.map(function (k) { return [k, k, coN[k]]; }), st.country, 'country', null, 'origin');
    }
    if (ddOrigin) dds.appendChild(ddOrigin);
    var ddTr = chipsDropdown('Treatment', 'where known',
      trKeys.map(function (k) { return [k, k, trN[k]]; }), st.treatment, 'treatment', null,
      // ⚠️ COLORED TAB ONLY. The treatment copy is about heat and emerald oil; on the
      // diamond tab this row means "Color Enhanced", which that text does not describe.
      KIND !== 'diamond' ? 'treatment' : null);
    if (ddTr) dds.appendChild(ddTr);

    var ddSet = chipsDropdown('Can be set', 'which builder can use this stone',
      setKeys.map(function (k) { return [k, k, setN[k]]; }), st.setting, 'setting');
    if (ddSet) dds.appendChild(ddSet);

    // CUT STYLE. ⚠️ "Faceted" IS EXCLUDED, AND ONLY "Faceted". It is 6,177 of 7,890
    // (78.3%) and means no more than "this is a normal stone" — the landing pages
    // drop it for the same reason at 73%. Every other value stays, including the
    // ones nobody has heard of: this list is built from what the shards contain,
    // so Riente (14) and Duchess Faceted-Cut (13) appear without being named, and
    // whatever Stuller adds next will too. An INCLUSION list is how a facet
    // silently stops showing new stock.
    var csN = {};
    poolFor('cutstyle').forEach(function (r) {
      if (r.cutStyle) csN[r.cutStyle] = (csN[r.cutStyle] || 0) + 1;
    });
    var csKeys = Object.keys(csN).sort(function (a, b) { return csN[b] - csN[a]; });
    var ddCut = chipsDropdown('Cut style', null,
      csKeys.map(function (k) { return [k, k, csN[k]]; }), st.cutstyle, 'cutstyle',
      // ⚠️ THE SAME THREE stone-browse.liquid SKIPS, not just the first. "Faceted"
      // is 73% of the catalogue and means no more than "this is a normal stone";
      // Faceted Diamond-Cut and Precision Diamond-Cut are Stuller's words for the
      // same statement. This panel dropped only "faceted", so the other two showed
      // here and nowhere else — the same facet answering differently on two pages
      // (owner, 2026-08-10). It stays an EXCLUSION list: driving it off cuts to
      // SHOW would silently hide whatever Stuller adds next.
      ['faceted', 'faceted diamond-cut', 'precision diamond-cut']);
    if (ddCut) dds.appendChild(ddCut);

    // QUALITY. The supplier ladder on calibrated stones plus the one-of-a-kind
    // bucket, matching the row the landing pages render through `sb-dd-chips` —
    // same facet, same order, same sub-line, so the two surfaces cannot answer
    // differently (the defect Cut style carried until 2026-08-10).
    //
    // 🚨 THE SENTINEL LEADS, AND ORDER IS THE POINT OF THIS ROW. Sorted by
    // frequency or alphabetically, "One of a kind" lands mid-list under two-letter
    // grades — on the one facet whose whole risk is a shopper choosing AAA and
    // never learning that every certificated one-of-a-kind stone was just excluded.
    // QUALITY_ORDER is the ladder, best first; anything unrecognised keeps its place
    // at the end rather than being dropped, so a value Stuller adds still shows.
    // ⚠️ FROM THE ENGINE, NOT A LOCAL COPY. Three surfaces order this facet and a
    // second array is the two-copies failure this repo already pays for elsewhere.
    var QUALITY_ORDER = RB.QUALITY_ORDER;
    var qlN = {};
    poolFor('quality').forEach(function (r) {
      if (r.quality) qlN[r.quality] = (qlN[r.quality] || 0) + 1;
    });
    var qlKeys = Object.keys(qlN).sort(function (a, b) {
      var ia = QUALITY_ORDER.indexOf(a), ib = QUALITY_ORDER.indexOf(b);
      if (ia < 0) ia = QUALITY_ORDER.length;
      if (ib < 0) ib = QUALITY_ORDER.length;
      return ia - ib || a.localeCompare(b);
    });
    // ⚠️ A ONE-VALUE ROW IS A DEAD FACET and this one really does collapse to a
    // single value on the diamond tab: a serialized diamond is one-of-a-kind and a
    // calibrated diamond's "quality" is a shelf name, not a grade. Gated on the
    // DATA rather than on the tab, so it appears by itself the day diamonds carry
    // real grades, and disappears by itself if the coloured ones stop.
    var ddQual = qlKeys.length > 1 ? chipsDropdown('Quality',
      'calibrated stones carry a grade; one-of-a-kind stones are described individually',
      qlKeys.map(function (k) { return [k, k, qlN[k]]; }), st.quality, 'quality', null,
      // The quality copy describes colored-stone grades; see the treatment note above.
      KIND !== 'diamond' ? 'quality' : null) : null;
    if (ddQual) dds.appendChild(ddQual);

    // LENGTH / WIDTH. Length is the LONG dimension and width the short; they fall
    // in different bands on most stones, which is what makes the second control
    // worth having — a shopper matching a stone to a setting is matching both.
    // 100% filled on this population, so the "mm, every stone" qualifier the
    // landing pages use is literally true here.
    function mmDropdown(label, lo, hi, min, max, keys) {
      var applied = (lo > min || hi < max)
        ? [lo.toFixed(2) + '–' + hi.toFixed(2) + ' mm'] : [];
      // Same factory as price and carat, and the ratio test decides per POOL
      // rather than per axis — which is the point of having a threshold instead
      // of a hardcoded choice. Measured 2026-08-10:
      //     diamond length  3.50-23.49 mm   6.7x  -> linear
      //     diamond width   3.08-15.37 mm   5.0x  -> linear
      //     colored width   1.00-19.41 mm  19.4x  -> linear
      //     colored LENGTH  0.90-559.0 mm 621.1x  -> LOG, and it needs it: on a
      //                     linear track the median 7 mm stone sits at 1.1%.
      // ⚠️ An earlier version of this comment asserted millimetres are always
      // linear. Three of the four are; the fourth is worse than price.
      // ⚠️ THE CONTROL ONLY — the row's label column is the <summary> above (the
      // split note on rangeControl). `RB.contRow` is the one place on the store
      // that resolves SRScale (verify_slider_scales.py), so the engine builds the
      // row and the control is lifted out of it rather than mapping millimetres to
      // positions a second time here. The readout is exact until a handle moves,
      // for the reason in rangeControl; linear or log is decided per pool by the
      // ratio table above, and a physical dimension is one of the few axes where
      // even travel is what a shopper expects. Only the readout was ever wrong here.
      var mmFmt = { raw: function (v) { return v.toFixed(2); },
                    show: function (v) { return v.toFixed(2) + ' mm'; } };
      var ctrl = RB.contRow(label, null, lo, hi, min, max,
        function (a, b) {
          var patch = {};
          patch[keys[0]] = mmFmt.raw(a);
          patch[keys[1]] = mmFmt.raw(b);
          apply(patch);
        }, mmFmt).querySelector('.sb-range');
      return RB.dropdown(label, RB.ddValue(applied), applied.length > 0, [
        el('p', { class: 'sb-dd__sub', text: 'mm, every stone' }),
        // stone-browse.css sizes the control inside a popup through
        // `.sb-dd__panel .sb__ranged { width: 100% }`, so the wrapper is not
        // decoration — without it the track collapses to its content width.
        el('div', { class: 'sb__ranged' }, [ctrl])
      ]);
    }
    dds.appendChild(mmDropdown('Length', st.len[0], st.len[1], lMin, lMax,
                               ['lmin', 'lmax']));
    dds.appendChild(mmDropdown('Width', st.wid[0], st.wid[1], wMin, wMax,
                               ['wmin', 'wmax']));

    panel.appendChild(dds);
    wireDropdowns(panel);

    /* ------------------------------------------------------- count + chips */
    // `.sb__count strong` is stone-browse.css's — it inks the NUMBER against the
    // muted words around it, which is what makes a count scannable at the head of a
    // toolbar. The landing pages emit it; this was printing one flat string.
    countEl.innerHTML = '';
    countEl.appendChild(el('strong', { text: rows.length.toLocaleString() }));
    // ⚠️ THE NOUN FOLLOWS THE TAB. It was hardcoded to "diamond(s)", which is
    // correct on /collections/diamonds and a plain lie on the colored tab — the
    // grid showed garnets under "3,951 natural diamonds". A count that names the
    // wrong thing is worse than no count: it reads as the filter having failed.
    var noun = KIND === 'diamond'
      ? (rows.length === 1 ? 'diamond' : 'diamonds')
      : KIND === 'all' ? (rows.length === 1 ? 'stone' : 'stones')
      : (rows.length === 1 ? 'colored stone' : 'colored stones');
    // ⚠️ AND SO DOES THE FORMATION WORD. It was unconditional, so a grid showing
    // both read "6,110 natural diamonds" over a set that is 83% lab-grown — the
    // same class of lie as the noun above, and the reason the word is now dropped
    // whenever the shopper has not actually narrowed the formation.
    var form = originSel.length === 1
      ? (originSel[0] === 'lab' ? 'Lab-Grown ' : originSel[0] === 'cult' ? 'cultured ' : 'natural ') : '';
    countEl.appendChild(document.createTextNode(' ' + form + noun));
    // THE LANDING PAGE'S CARAT NOTE, on an all-kinds page, while a carat range is narrowed: without it a
    // result missing every calibrated stone reads as thin stock. stone-browse.liquid's words in American
    // spelling (the store rule; that file still says "millimetre") and its `.sb__note`; the link drops the
    // carat range and keeps everything else chosen.
    if (ALL_KINDS) {
      var foot = countEl.parentNode, note = foot && foot.querySelector('[data-db-carat-note]');
      if (caratOn && foot && !note) {
        note = el('p', { class: 'sb__note', 'data-db-carat-note': '' });
        note.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true">' +
          '<circle cx="12" cy="12" r="9"/><path d="M12 8h.01M11 12h1v4h1"/></svg>';
        note.appendChild(el('span', null, [
          document.createTextNode('Stones cut to a standard millimeter size carry no carat weight, so they ' +
                                  'are not in this result. '),
          el('a', { href: hrefWith({ ctmin: null, ctmax: null }), text: 'Filter by size instead' }),
          document.createTextNode('.')]));
        foot.appendChild(note);
      } else if (note && caratOn) {
        note.querySelector('a').setAttribute('href', hrefWith({ ctmin: null, ctmax: null }));
      } else if (note) {
        note.parentNode.removeChild(note);
      }
    }

    activeEl.innerHTML = '';
    var chips = [];
    // 🚨 THE ACTIVE-FILTER ROW IS THE LANDING PAGES' OWN MARKUP, NOT AN INVENTION.
    // stone-browse.liquid renders `<a class="sb-active"><span class="sb-active__k">
    // FACET</span> value <svg/><span class="visually-hidden">Remove filter</span></a>`
    // and closes with `<a class="sb__clear">Clear all</a>`, all inside `.sb__active`.
    // The first cut here used `.sb__cb` — which is the panel's MOBILE COLLAPSE
    // CHECKBOX, not a chip — so the row rendered unstyled and read as missing
    // entirely. They are LINKS, not buttons: each href drops one facet and keeps the
    // rest, which is what makes them work on a middle-click or a share, and it
    // matches every other landing page.
    function chip(key, value, patch) { chips.push([key, value, patch]); }
    // 🚨 FORMATION EARNED A CHIP THE DAY IT BECAME OPTIONAL. While exactly one was
    // always applied there was nothing to remove and no chip made sense; now that
    // "both" is a real state, a selected formation is an ordinary narrowing and has
    // to be visible and removable like the rest — otherwise it is a filter with no
    // × again, which is precisely the trap Shape was pulled out of.
    originSel.forEach(function (o) {
      chip('Formation', FORM_LABEL[o],
           { origin: originSel.filter(function (x) { return x !== o; }).join(',') || null });
    });
    if (st.col[0] > 0 || st.col[1] < COLOR.length)
      chip('Color', COLOR[st.col[1] - 1] + '–' + COLOR[st.col[0]], { colmin: null, colmax: null });
    if (st.cla[0] > 0 || st.cla[1] < CLARITY.length)
      chip('Clarity', CLARITY[st.cla[1] - 1] + '–' + CLARITY[st.cla[0]], { clmin: null, clmax: null });
    if (st.cut[0] > 0 || st.cut[1] < CUT.length)
      chip('Cut', CUT[st.cut[0]] + ' and better', { cutmin: null, cutmax: null });
    if (st.carat[0] > cMin || st.carat[1] < cMax)
      chip('Carat', st.carat[0].toFixed(2) + '–' + st.carat[1].toFixed(2) + ' ct',
           { ctmin: null, ctmax: null });
    if (st.price[0] > pMin || st.price[1] < pMax)
      chip('Price', money(st.price[0]) + ' – ' + money(st.price[1]), { pmin: null, pmax: null });
    // ⚠️ ONE CHIP PER RANGE, NOT ONE PER END. The landing pages learned this the
    // painful way (owner, 2026-08-02): a band filter printed nineteen chips and each
    // × punched a hole out of the middle of the shopper's own range. Both millimetre
    // bounds are cleared together, which is the only sane meaning of the ×.
    if (st.len[0] > lMin || st.len[1] < lMax)
      chip('Length', st.len[0].toFixed(2) + '–' + st.len[1].toFixed(2) + ' mm',
           { lmin: null, lmax: null });
    if (st.wid[0] > wMin || st.wid[1] < wMax)
      chip('Width', st.wid[0].toFixed(2) + '–' + st.wid[1].toFixed(2) + ' mm',
           { wmin: null, wmax: null });
    // These two ARE independent choices, so they get one removable chip each —
    // the same split the landing pages make between a banded range and a chip list.
    st.setting.forEach(function (v) {
      chip('Can be set', v,
           { setting: st.setting.filter(function (x) { return x !== v; }).join(',') || null });
    });
    st.cutstyle.forEach(function (v) {
      chip('Cut style', v,
           { cutstyle: st.cutstyle.filter(function (x) { return x !== v; }).join(',') || null });
    });
    st.treatment.forEach(function (v) {
      chip('Treatment', v,
           { treatment: st.treatment.filter(function (x) { return x !== v; }).join(',') || null });
    });
    st.quality.forEach(function (v) {
      chip('Quality', v,
           { quality: st.quality.filter(function (x) { return x !== v; }).join(',') || null });
    });
    // 🚨 SPECIES, COLOR AND ORIGIN HAD NO CHIPS, AND THE HUB NOW LINKS STRAIGHT INTO
    // THEM. The gemstone hub's "Shop by color" swatches open
    // `?gem=color&hue=Blue` (owner, 2026-08-10), so a shopper can now ARRIVE with a
    // facet already applied that they never pressed — and until this, the only sign
    // of it was one swatch looking selected in a scrolling row further up the panel.
    // A filter with no × is the trap the shape row and the formation row were both
    // pulled out of; these three are the last of them.
    st.sp.forEach(function (v) {
      chip('Stone', v, { sp: st.sp.filter(function (x) { return x !== v; }).join(',') || null });
    });
    st.hue.forEach(function (v) {
      chip('Color', v, { hue: st.hue.filter(function (x) { return x !== v; }).join(',') || null });
    });
    st.country.forEach(function (v) {
      chip('Origin', v, { country: st.country.filter(function (x) { return x !== v; }).join(',') || null });
    });
    st.fancy.forEach(function (h) {
      chip('Fancy color', h,
           { fancy: st.fancy.filter(function (x) { return x !== h; }).join(',') || null });
    });
    st.off.forEach(function (g) {
      chip('Other color', g,
           { offcolor: st.off.filter(function (x) { return x !== g; }).join(',') || null });
    });
    // Each ticked shape gets its own removable chip, so a shopper who has picked
    // four can drop one without hunting for its tile in a scroller.
    picked.forEach(function (x) {
      chip('Shape', titleCase(x),
           { shape: picked.filter(function (y) { return y !== x; }).join(',') || null });
    });

    function xIcon() {
      var s = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      s.setAttribute('viewBox', '0 0 12 12');
      s.setAttribute('aria-hidden', 'true');
      s.innerHTML = '<path d="M2 2l8 8M10 2l-8 8"></path>';
      return s;
    }
    chips.forEach(function (c) {
      var a = el('a', { class: 'sb-active', href: hrefWith(c[2]) }, [
        el('span', { class: 'sb-active__k', text: c[0] })
      ]);
      a.appendChild(document.createTextNode(' ' + c[1] + ' '));
      a.appendChild(xIcon());
      a.appendChild(el('span', { class: 'visually-hidden', text: 'Remove filter' }));
      activeEl.appendChild(a);
    });
    if (chips.length) {
      // Clear all goes to the BARE collection URL, exactly as the landing pages do —
      // it drops origin and shape too, because "clear all" that quietly kept two
      // filters on is the kind of half-reset a shopper has to undo by hand.
      // 🚨 BARE MEANS THIS TOOL'S BARE ADDRESS, NOT `location.pathname` (2026-09-16). On
      // gem search the bare collection URL is the gemstone HUB — `view` is what makes it
      // this tool — so Clear all took the shopper out of the search altogether, the same
      // defect the `resetHref` note above records for the Colored stones switch. Measured
      // on live /collections/all-gemstones?view=stone-search&gem=color&hue=Blue: Clear all
      // pointed at /collections/all-gemstones. The Diamonds / Colored stones tab is the
      // page they are on (`is-current`), not a filter, so it stays too; on
      // /collections/diamonds there is neither, and the link is the bare URL as before.
      activeEl.appendChild(el('a', {
        class: 'sb__clear', href: resetHref({ gem: gemKeep() }),
        'data-sr-reset': '', text: 'Clear all'
      }));
    }

    // SORT. ⚠️ THE <select> IS NO LONGER BUILT HERE — it is authored in
    // sections/diamond-search.liquid, inside the theme's own `.select` wrapper and
    // wearing `.facet-filters__sort.select__select.caption-large`. That is what
    // makes it the same SIZE as the landing pages' control: the theme's
    // `.select__select` (padding 1rem 4rem 1rem 2rem, height 38.64px) out-specifies
    // stone-browse.css's `.sb__tools select`, so a bare <select> built here came out
    // visibly smaller than the one on /collections/sapphires. This only marks the
    // current option and binds the change.
    //
    // `.value =` rather than a `selected` attribute: the markup is already in the
    // DOM by the time this runs, and setting the property is what actually moves a
    // live <select>.
    if (sortEl) {
      sortEl.value = st.sort;
      // A URL carrying a sort this select has no option for (an old `price-asc`
      // link, say) leaves `.value` empty and the control reads blank while the grid
      // is correctly sorted. Show the option the alias resolves to instead.
      if (!sortEl.value) {
        sortEl.value = (st.sort === 'price-desc') ? 'price-descending'
                     : (st.sort === 'price-asc') ? 'price-ascending' : 'manual';
      }
      // ⚠️ BOUND ONCE (2026-09-16): the <select> is the section's and survives every redraw.
      if (!sortEl.hasAttribute('data-db-sort-wired')) {
        sortEl.setAttribute('data-db-sort-wired', '');
        sortEl.addEventListener('change', function () { apply({ sort: this.value }); });
      }
    }

    /* --------------------------------------------------------------- grid */
    // Cards use the THEME's own card classes, so they inherit the collection
    // grid's styling rather than carrying a second card design. Liquid's
    // card-product snippet cannot be called from JS, so this is the closest
    // faithful equivalent — same wrappers, same typography, same link.
    grid.innerHTML = '';
    if (!rows.length) {
      // The previous result's "Show more" sits beside the grid, not in it; draw() removes it, and this
      // branch returns before draw().
      var staleMore = root.querySelector('.db-more');
      if (staleMore) staleMore.remove();
      mountListHead(rows);   // takes down a list header left from a result that had rows
      grid.appendChild(el('li', { class: 'db-empty' }, [
        el('p', { text: 'No ' + (KIND === 'diamond' ? 'diamonds' : KIND === 'all' ? 'stones' : 'colored stones')
                        + ' match those filters.' }),
        // Same `.sb__clear` control as the active row, so the way out of an empty
        // result set looks like the way out of any other filter state — including
        // where it goes (see Clear all above).
        el('a', { class: 'sb__clear', href: resetHref({ gem: gemKeep() }),
                  'data-sr-reset': '', text: 'Clear all filters' })
      ]));
      return;
    }

    var PAGE = 48, shown = 0;
    function draw() {
      rows.slice(shown, shown + PAGE).forEach(function (r) {
        // `S.colourLabel`, not `r.color` — the raw column is a two-letter trade
        // code on a coloured stone ('Bl'), which is what the owner found on the
        // builder's spec table. Diamonds keep their D-Z grade.
        var spec = [r.carat ? r.carat.toFixed(2) + ' ct' : null, RB.colourLabel(r), r.clarity, r.cut]
          .filter(Boolean).join(' · ');
        var href = '/products/' + r.handle;
        // The viewer is a SIBLING of the link, inside .card-product__media-inner —
        // the same place card-stone-spin.liquid puts it. Nested inside the <a> the
        // badge's click would navigate before the viewer could handle it.
        var media = el('div', { class: 'card-product__media relative media-fit-fill' }, [
          el('div', { class: 'card-product__media-inner' }, [
            el('a', { class: 'product-link block', href: href, title: r.title }, [
              // 🚨 THE IMAGE MUST BE WRAPPED IN `<responsive-image>` WITH AN
              // `--aspect-ratio`, OR THE CARD COLLAPSES. The theme positions card
              // images out of flow, so a bare <img> contributes ZERO height: every
              // grid row sized to its text alone (95px), the picture overflowed into
              // the row beneath, and the bottom of each card looked cut off. The
              // wrapper is what reserves the box before the image loads. Stone
              // masters are square, hence 100%.
              r.img
                ? el('responsive-image', {
                    class: 'responsive-image product-first-image',
                    style: '--aspect-ratio:100.0%'
                  }, [
                    // 🚨 DO NOT HARDCODE `img-loaded`. The theme fades a card image
                    // in with an `image-fade-in` keyframe that runs scale(1.1) ->
                    // scale(1), driven by the lazy-image class flipping from
                    // `img-loading` to `img-loaded`. Shipping `img-loaded` on a
                    // freshly created node made the theme's loader ALSO add
                    // `img-loading`, so the element carried both, the animation never
                    // resolved, and it sat at **scale(1.1) forever** — the picture
                    // rendering 332px inside a 302px `overflow:hidden` box and
                    // quietly clipping ~10% off every edge, girdle and watermark
                    // included. An animation outranks even an inline style, which is
                    // why `transform: none` could not override it. Emit exactly what
                    // the theme emits and let it settle; `markLoaded` below closes
                    // the one gap that leaves — an image already in cache, whose
                    // `load` event fired before any listener existed.
                    imgEl(r)
                  ])
                : el('span', { class: 'db-noimg', text: 'Photograph to follow' })
            ]),
            spinViewer(r),
            // After the viewer, as card-product.liquid places it: `.shs ~ .quick-add` keeps it above a playing 360°.
            quickAdd(href)
          ])
        ]);
        var content = el('div', { class: 'card__content pt-6' }, [
          el('div', { class: 'card__information text-center' }, [
            el('div', { class: 'card__heading mb-2' }, [
              el('a', { href: href, title: r.title,
                        class: 'full-unstyled-link product-link', text: r.title })
            ]),
            el('div', { class: 'card-information' }, [
              el('div', { class: 'db-spec', text: spec }),
              el('div', { class: 'price' }, [
                el('div', { class: 'price__container' }, [
                  el('div', { class: 'price__regular' }, [
                    el('span', { class: 'price-item price-item--regular',
                                 text: money(r.price) })
                  ])
                ])
              ])
            ])
          ])
        ]);
        grid.appendChild(el('li', { class: 'grid__item' }, [
          el('div', { class: 'card-wrapper product-card-wrapper' }, [
            el('div', { class: 'card-product' }, [media, content, listCells(r)])
          ])
        ]));
      });
      shown += PAGE;
      var old = root.querySelector('.db-more');
      if (old) old.remove();
      if (shown < rows.length) {
        grid.parentNode.appendChild(el('button', {
          class: 'btn btn-outline db-more', type: 'button',
          text: 'Show more',
          onclick: draw
        }));
      }
    }
    draw();
    mountListHead(rows);
  });

  /* ==========================================================================
     LIST VIEW + COMPARE — the cells stone-list.js expects to find
     ==========================================================================
     `assets/stone-list.js` owns the behaviour: the layout switch, the localStorage
     selection, the tray and the compare overlay. It is loaded unchanged and shared
     with the 21 species landing pages. What it needs from a page is MARKUP, and on
     those pages Liquid supplies it (`snippets/card-stone-row.liquid`) inside the
     theme's own `.grid__item` — which is load-bearing there, because that template
     paginates with infinite scroll and rows therefore arrive already wired.

     🚨 THAT ASSUMPTION IS THE ONE THING THAT DOES NOT PORT. This grid has no Liquid
     rows at all: every card is built here. So the CELLS are rebuilt below from the
     shard columns, and everything else — the payload format, the drawer, the
     re-tick-on-append — is reused rather than reimplemented. A second compare
     implementation is the chain_fit bug waiting to happen; a second row renderer is
     unavoidable.

     Appended as the THIRD child of `.card-product`, exactly where card-product.liquid
     renders `card-stone-row`, because stone-list.css depends on that position twice:
     in grid view `.sbl` is a block and only `.sbl__c--cmp` shows, so the checkbox
     lands under the price; in list view `.card-wrapper`, `.card-product` and `.sbl`
     all become `display: contents`, which flattens the media box, the title block
     and these cells into ONE row grid whose tracks the section declares. */

  // ⚠️ CELL ORDER IS COLUMN ORDER. They are direct grid items in list view, so DOM
  // order IS the column they land in, and it has to match `--sbl-cols` and the
  // header in sections/diamond-search.liquid: photo, stone, then these eight.
  //
  // ⚠️ EVERY CELL RENDERS EVEN WHEN ITS VALUE IS MISSING — card-stone-row.liquid's
  // rule and the reason is structural: the track list is fixed, so skipping a blank
  // cell shifts every cell after it one column left on that row only, and the
  // columns silently stop lining up on exactly the rows with incomplete data. An
  // em-dash is also the honest rendering.
  function cell(mod, label, kids) {
    return el('div', { class: 'sbl__c sbl__c--' + mod, 'data-l': label },
      kids && kids.length ? kids : [el('span', { class: 'sbl__none', text: '—' })]);
  }
  function textCell(mod, label, value) {
    return cell(mod, label, value ? [document.createTextNode(String(value))] : null);
  }

  function listCells(r) {
    var shapeName = titleCase(r.shape);
    var ico = shapeIcon(r.shape);

    // THE COMPARE PAYLOAD TRAVELS WITH THE CARD, in card-stone-row.liquid's exact
    // key set, because stone-list.js reads it by name and knows nothing about which
    // page wrote it. A shopper ticks a stone, changes the filter, ticks another —
    // the second render knows nothing about the first, which is not on the page any
    // more — so the overlay is rebuilt from storage and everything it needs has to
    // have been captured at tick time.
    //
    // `id` IS THE HANDLE, not the variant id. It only has to be unique and stable,
    // it is what the URL already uses, and it cannot collide with the numeric
    // product ids the landing pages store — which matters, because compare is
    // deliberately cross-page and a shopper can hold a sapphire and a diamond at
    // once.
    //
    // `ct` / `cl` / `cg` are the diamond additions: carat, clarity and cut are the
    // columns here and stone-list.js draws each row only when some stone in the set
    // carries it, so they are invisible on a coloured-stone comparison.
    //
    // Carat is stored PRE-FORMATTED ("1.50", not 1.5) because the overlay prints it
    // verbatim and the card beside it prints two decimals; `bestIndex` parses it
    // back with parseFloat, so ranking is unaffected.
    //
    // The SILHOUETTE is not in here. stone-list.js reads it off this row's own
    // `.sbl__ico svg` when the box is ticked — a stone can only be selected from a
    // page that drew it — so `gem-shape-icon.liquid` stays the single source of
    // those paths and 48 cards do not each carry a duplicated copy.
    var cmp = {
      id: r.handle, u: '/products/' + r.handle, t: r.title,
      img: sized(r.img, 400) || '', spin: r.video || '',
      c: RB.colourLabel(r) || '', l: r.l || '', w: r.w || '',
      sh: shapeName, g: r.species || 'Diamond', wr: r.wear || '',
      // FORMATION, NOT MINING ORIGIN. The landing pages' `or` key is a country and
      // diamonds carry none, so that row correctly never appears; `f` is
      // Natural/Lab-Grown from the SHARD the stone was loaded out of (the fact
      // `custom.stone_origin` holds), and Cultured for a pearl, whichever shard it sits
      // in: a cultured pearl is never "natural" (generate_stone_products.tags_for).
      f: FORM_LABEL[formOf(r)],
      ct: r.carat ? r.carat.toFixed(2) : '', cl: r.clarity || '', cg: r.cut || '',
      // 🚨 `pn` IS IN CENTS, BECAUSE THE OTHER PRODUCER OF THIS PAYLOAD IS LIQUID.
      // `card-stone-row.liquid` writes `"pn": {{ card_product.price | json }}` and
      // Shopify's `product.price` is an INTEGER OF CENTS, while this shard's `p`
      // column is DOLLARS (`round(float(variant.price), 2)`). The compare tray mixes
      // both — a shopper adds sapphires on a species page and a diamond here — and
      // `stone-list.js` compares `num(p.pn)` raw, so an $11,625 diamond (11625)
      // scored BELOW a $545 sapphire (54500) and was labelled "lowest". Owner found
      // it 2026-08-06 comparing a diamond against two sapphires.
      // ⚠️ The unit is arbitrary but must MATCH; cents wins because it is Shopify's
      // own and 21 landing pages already emit it against this one page.
      p: money(r.price), pn: Math.round(r.price * 100), av: r.available
    };

    return el('div', { class: 'sbl' }, [
      cell('ct', 'Carat', r.carat ? [el('span', { class: 'sbl__mm' }, [
        document.createTextNode(r.carat.toFixed(2)),
        el('span', { class: 'sbl__u', text: 'ct' })
      ])] : null),
      textCell('co', 'Color', RB.colourLabel(r)),
      textCell('cl', 'Clarity', r.clarity),
      textCell('cg', 'Cut', r.cut),
      // ONE CELL FOR BOTH DIMENSIONS — "6.43 × 6.40 mm" is the number a shopper
      // matches to a setting, and two columns of near-identical figures read as
      // noise. Width degrades on its own rather than taking the length with it.
      cell('mm', 'Size', r.l ? [el('span', { class: 'sbl__mm' }, [
        document.createTextNode(String(r.l)),
        r.w ? el('span', { class: 'sbl__x', 'aria-hidden': 'true', text: '×' }) : null,
        r.w ? document.createTextNode(String(r.w)) : null,
        el('span', { class: 'sbl__u', text: 'mm' })
      ].filter(Boolean))] : null),
      cell('sh', 'Shape', shapeName
        ? [ico ? el('span', { class: 'sbl__ico' }, [ico]) : null,
           el('span', { class: 'sbl__sht', text: shapeName })].filter(Boolean)
        : null),
      cell('pr', 'Price', [el('span', { class: 'sbl__pr', text: money(r.price) })]),
      // `no-js-hidden` because compare is entirely client-side: with JS off the box
      // would tick and do nothing, which is worse than not offering it. The input is
      // a REAL checkbox inside a <label>, so the click target, keyboard operation
      // and the checked announcement all come from the browser.
      el('div', { class: 'sbl__c sbl__c--cmp no-js-hidden' }, [
        el('label', { class: 'sbl-cmp' }, [
          el('input', { type: 'checkbox', class: 'sbl-cmp__i',
                        'data-cmp': JSON.stringify(cmp),
                        'aria-label': 'Compare ' + r.title }),
          el('span', { class: 'sbl-cmp__box', 'aria-hidden': 'true',
                       html: '<svg viewBox="0 0 12 12" aria-hidden="true">'
                           + '<path d="M2 6.4l2.6 2.6L10 3.4"></path></svg>' }),
          el('span', { class: 'sbl-cmp__t', text: 'Compare' })
        ])
      ])
    ]);
  }

  /* THE COLUMN HEADER, cloned out of the <template> the section authors.

     ⚠️ IT IS MOUNTED HERE, NOT BY stone-list.js, AND THE DIFFERENCE IS A RACE.
     That file does the insert on DOMContentLoaded, guarded on the grid already
     holding an `li.grid__item` — true on a landing page, where Liquid rendered the
     rows server-side, and false here, where the grid holds loading skeletons until
     the shards come back over the network. So its attempt is a correct no-op and
     this runs after the first draw instead. Both guard on `[data-sbl-head]`, so
     whichever gets there first wins and neither can double it.

     Skipped on an empty result, as it is there: a column header over no rows reads
     as a broken table, and the panel's own empty state already explains the zero. */
  function mountListHead(rows) {
    // ⚠️ ON EVERY DRAW, NOT ONCE (2026-09-16). render() now runs on every filter, so a
    // header mounted over one result would sit on over an empty one, and the caption
    // below would go on describing stones no longer on screen.
    var head = document.querySelector('[data-sbl-head]');
    if (!rows.length) {
      if (head) head.remove();
      return;
    }
    var tpl = document.getElementById('sb-list-head');
    if (!head && tpl) grid.parentNode.insertBefore(tpl.content.cloneNode(true), grid);

    // DURABILITY, PRINTED ONCE — filled from the pool rather than typed into the
    // template, so the line cannot drift from the stones the same page is drawing.
    // It is `custom.wearability`: 100% filled on all 7,890 diamonds and carrying
    // exactly ONE value, which is what makes it a caption and not a facet. The <p>
    // stays hidden unless the pool really does agree on one value — on a mixed set
    // "Every diamond here: …" would be a claim rather than a summary.
    var cap = document.querySelector('[data-db-wear]');
    if (!cap) return;
    // A mixed page must not print a durability claim (diamond at Mohs 10 beside pearl at 2.5).
    if (ALL_KINDS && KIND !== 'diamond') { cap.hidden = true; return; }
    var vals = {};
    rows.forEach(function (r) { if (r.wear) vals[r.wear] = 1; });
    var only = Object.keys(vals);
    cap.hidden = only.length !== 1;
    if (only.length === 1) cap.querySelector('[data-db-wear-v]').textContent = only[0];
  }

  }   // end of render() — see the note where it opens

  render(FIRST);
})();
